import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toPng } from 'html-to-image';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet, TrendingUp, Shield, Cpu, Activity, DollarSign,
  LayoutDashboard, Home, ArrowUpRight, ArrowDownLeft,
  Settings, LogOut, PieChart, Clock, Zap, X, Copy, Download, TrendingDown,
  ChevronLeft, ChevronRight, AlertTriangle, Check, Sparkles, Lock, Award, Package, Play
} from 'lucide-react';
import { metaApiService } from './services/metaApi';

const SITE_URL = 'https://flexbot.ai';
const SEO = ({ title, description, path = '/', image = `${SITE_URL}/FLEX.png` }) => {
  const url = `${SITE_URL}${path}`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

// --- Shared hook: fetch + dedupe master account trades since live start ---
const SERVER_URL = 'https://flexbot-qpf2.onrender.com';
const LIVE_START_MS = new Date('2026-05-12T00:00:00Z').getTime();
const START_BALANCE = 100000;
const parseResult = (r) => parseFloat(String(r).replace(/[^0-9.\-+]/g, '')) || 0;

// Filter trades from /api/trades response for a specific account login.
// Accepts trades with null result (older EA versions) — stats fall back to outcome-based.
const filterTradesForLogin = (allTrades, login) => {
  const prefix = `m-${login}-`;
  const seen = new Set();
  return (allTrades || []).filter(t => {
    const id = String(t.id || '');
    const outcome = String(t.outcome || '');
    if (['SIM_CLOSE', 'ADMIN_CLOSE'].includes(outcome)) return false;
    if (!id.startsWith(prefix)) return false;
    if (id.toLowerCase().includes('test')) return false;
    if (outcome.toLowerCase().includes('test')) return false;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  }).sort((a, b) => (a.closed_at || 0) - (b.closed_at || 0));
};

// Classify a trade as win/loss, preferring numeric result, falling back to outcome.
const tradeOutcome = (t) => {
  const r = parseResult(t.result);
  if (r !== 0) return r > 0 ? 'win' : 'loss';
  const o = String(t.outcome || '').toLowerCase();
  if (o.includes('tp')) return 'win';
  if (o.includes('sl')) return 'loss';
  return 'unknown';
};

// Compute account metrics from a set of trades + starting balance.
// If realAccount is provided (master account match), uses real equity/balance
// for the bottom-line numbers — captures commission/swap that trade results miss.
const computeStatsFromTrades = (trades, startBalance, realAccount = null) => {
  const classed = trades.map(t => ({ ...t, _outcome: tradeOutcome(t), _r: parseResult(t.result) }));
  const wins = classed.filter(t => t._outcome === 'win');
  const losses = classed.filter(t => t._outcome === 'loss');
  const classified = wins.length + losses.length;
  const tradeSum = classed.reduce((s, t) => s + t._r, 0);
  const grossWin = classed.filter(t => t._r > 0).reduce((s, t) => s + t._r, 0);
  const grossLoss = Math.abs(classed.filter(t => t._r < 0).reduce((s, t) => s + t._r, 0));
  // Prefer real account equity if available (covers commission/swap).
  const realEquity = realAccount?.equity;
  const realBalance = realAccount?.balance ?? realEquity;
  const equity = realEquity != null ? realEquity : startBalance + tradeSum;
  const balance = realBalance != null ? realBalance : startBalance + tradeSum;
  const totalProfit = equity - startBalance;
  const hasPL = classed.some(t => t._r !== 0) || realEquity != null;

  const dayMap = {};
  classed.forEach(t => {
    const d = new Date(t.closed_at);
    d.setUTCHours(0, 0, 0, 0);
    const k = d.toISOString().slice(0, 10);
    dayMap[k] = (dayMap[k] || 0) + t._r;
  });
  const dailyGrowth = Object.entries(dayMap).sort().map(([date, pnl]) => ({
    date,
    profitPercentage: (pnl / startBalance) * 100,
  }));

  // Map trades into the shape the existing Dashboard history table expects.
  const mappedTrades = classed.map(t => ({
    id: t.id,
    symbol: t.symbol || 'XAUUSD',
    type: t.direction || 'BUY',
    profit: t._r,
    outcome: t.outcome,
    time: new Date(t.closed_at || Date.now()).toISOString(),
    opened_at: t.opened_at,
    closed_at: t.closed_at,
    entry_price: t.entry_price,
    sl: t.sl,
    tp: t.tp,
  }));

  return {
    balance,
    equity,
    profit: 0,
    trades: [],
    hasPL,
    metrics: {
      totalProfit,
      totalTrades: trades.length,
      winRate: classified > 0 ? (wins.length / classified) * 100 : 0,
      maxDrawdown: hasPL ? Math.max(0, (startBalance - equity) / startBalance * 100) : 0,
      profitFactor: grossLoss > 0 ? grossWin / grossLoss : 0,
      averageWin: wins.length > 0 && grossWin > 0 ? grossWin / wins.length : 0,
      averageLoss: losses.length > 0 && grossLoss > 0 ? -grossLoss / losses.length : 0,
      deposits: startBalance,
      withdrawals: 0,
      trades: mappedTrades,
      dailyGrowth,
    },
  };
};

const useLiveTrades = () => {
  const [data, setData] = useState({ trades: [], account: null, loading: true });
  useEffect(() => {
    let cancelled = false;
    const fetchTrades = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/trades?limit=2000`);
        const json = await res.json();
        if (!json.ok || cancelled) return;
        const activeLogin = json.account?.login ? String(json.account.login) : null;
        const seen = new Set();
        const trades = (json.trades || []).filter(t => {
          const id = String(t.id || '');
          const outcome = String(t.outcome || '');
          if (!t.result) return false;
          if (['closed', 'SIM_CLOSE', 'ADMIN_CLOSE'].includes(outcome)) return false;
          if ((t.closed_at || 0) < LIVE_START_MS) return false;
          if (id.startsWith('m-') && activeLogin && !id.startsWith(`m-${activeLogin}-`)) return false;
          if (id.toLowerCase().includes('test')) return false;
          if (outcome.toLowerCase().includes('test')) return false;
          if (id && seen.has(id)) return false;
          if (id) seen.add(id);
          return true;
        });
        // Sort chronologically (oldest first)
        trades.sort((a, b) => (a.closed_at || 0) - (b.closed_at || 0));
        setData({ trades, account: json.account || null, loading: false });
      } catch (e) {
        if (!cancelled) setData(d => ({ ...d, loading: false }));
      }
    };
    fetchTrades();
    const interval = setInterval(fetchTrades, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
  return data;
};

// --- Sound system: subtle UI sounds with localStorage toggle ---
const SOUND_KEY = 'flexbot_sound_enabled';
const playSound = (type) => {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(SOUND_KEY) !== '1') return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    const freq = type === 'click' ? 880 : type === 'hover' ? 1200 : 440;
    osc.frequency.value = freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    osc.start();
    osc.stop(ctx.currentTime + 0.08);
  } catch (e) { /* noop */ }
};

// --- Live Trade Ticker: sliding strip at top showing recent closed trades ---
const LiveTradeTicker = () => {
  const { trades } = useLiveTrades();
  if (!trades || trades.length === 0) return null;
  const recent = [...trades].reverse().slice(0, 15);
  const items = [...recent, ...recent]; // duplicate for seamless loop
  const fmtAgo = (ms) => {
    const d = Date.now() - ms;
    if (d < 60_000) return `${Math.floor(d / 1000)}s ago`;
    if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
    if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
    return `${Math.floor(d / 86_400_000)}d ago`;
  };
  return (
    <div className="relative w-full bg-black/60 backdrop-blur-md border-b border-white/5 overflow-hidden h-8 flex items-center">
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
      <div className="flex items-center gap-8 animate-[ticker_60s_linear_infinite] whitespace-nowrap">
        {items.map((t, i) => {
          const r = parseResult(t.result);
          const positive = r > 0;
          return (
            <div key={i} className="flex items-center gap-2 text-[11px] font-bold tracking-wide">
              <span className={`w-1.5 h-1.5 rounded-full ${positive ? 'bg-green-400' : 'bg-red-400'} ${i < 3 ? 'animate-pulse' : ''}`} />
              <span className="text-gray-500">XAUUSD</span>
              <span className={`${positive ? 'text-green-400' : 'text-red-400'}`}>
                {t.direction || 'BUY'} {positive ? '+' : ''}{r.toFixed(2)} USD
              </span>
              <span className="text-gray-600">· {fmtAgo(t.closed_at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Equity Curve Section: animated cumulative P/L since live start ---
const EquityCurveSection = () => {
  const { trades, account, loading } = useLiveTrades();
  if (loading || trades.length === 0) {
    return (
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 relative">
        <div className="text-center text-gray-500 text-sm font-bold">Loading live equity curve…</div>
      </section>
    );
  }

  // Distribute commission/swap (gap between trade sum and real equity) evenly per trade
  // so the curve endpoint matches the real account equity.
  const tradeSum = trades.reduce((s, t) => s + parseResult(t.result), 0);
  const realPL = account?.equity != null ? account.equity - START_BALANCE : tradeSum;
  const perTradeCost = (tradeSum - realPL) / trades.length;

  let running = START_BALANCE;
  const points = [{ x: trades[0]?.closed_at || Date.now(), y: running }];
  trades.forEach(t => {
    running += parseResult(t.result) - perTradeCost;
    points.push({ x: t.closed_at, y: running });
  });

  const minY = Math.min(...points.map(p => p.y));
  const maxY = Math.max(...points.map(p => p.y));
  const padY = (maxY - minY) * 0.1 || 1000;
  const yLow = minY - padY, yHigh = maxY + padY;
  const minX = points[0].x, maxX = points[points.length - 1].x;
  const rangeX = (maxX - minX) || 1;
  const W = 1000, H = 360;
  const sx = (x) => ((x - minX) / rangeX) * W;
  const sy = (y) => H - ((y - yLow) / (yHigh - yLow)) * H;
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.x).toFixed(1)} ${sy(p.y).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${sx(points[points.length - 1].x).toFixed(1)} ${H} L ${sx(points[0].x).toFixed(1)} ${H} Z`;

  const totalPL = running - START_BALANCE;
  const peakY = Math.max(...points.map(p => p.y));
  const finalColor = totalPL >= 0 ? '#10b981' : '#ef4444';

  const yTicks = [yLow, yLow + (yHigh - yLow) * 0.5, yHigh].map(v => Math.round(v / 1000) * 1000);
  const fmtDate = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const daysActive = Math.max(1, Math.floor((Date.now() - LIVE_START_MS) / 86_400_000));
  const activeLabel = daysActive < 7
    ? `${daysActive} day${daysActive === 1 ? '' : 's'}`
    : daysActive < 30
      ? `${Math.floor(daysActive / 7)} week${Math.floor(daysActive / 7) === 1 ? '' : 's'}`
      : `${Math.floor(daysActive / 30)} month${Math.floor(daysActive / 30) === 1 ? '' : 's'}`;

  return (
    <section className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 relative max-w-5xl">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/[0.04] via-transparent to-transparent pointer-events-none" />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5 relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-green-500/5 border border-green-500/10 text-green-500 text-[9px] font-black uppercase tracking-widest mb-2">
              <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse" /> Live MT5 Master
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase">Equity <span className="text-gray-500">Curve</span></h2>
            <p className="text-gray-500 text-xs font-medium mt-1">
              <span className="text-white font-bold">{activeLabel}</span> active · {trades.length} trades · since {fmtDate(LIVE_START_MS)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Cumulative P/L</p>
            <p className="text-2xl sm:text-3xl font-black tabular-nums" style={{ color: finalColor }}>
              {totalPL >= 0 ? '+' : '-'}${Math.abs(totalPL).toFixed(0)}
            </p>
          </div>
        </div>

        <div className="relative">
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-[180px] sm:h-[220px]">
            <defs>
              <linearGradient id="eq-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={finalColor} stopOpacity="0.35" />
                <stop offset="100%" stopColor={finalColor} stopOpacity="0" />
              </linearGradient>
              <linearGradient id="eq-line" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor={finalColor} />
              </linearGradient>
              <filter id="eq-glow">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {/* Grid lines */}
            {[0.25, 0.5, 0.75].map(t => (
              <line key={t} x1="0" y1={H * t} x2={W} y2={H * t} stroke="#ffffff" strokeOpacity="0.04" strokeDasharray="4 6" />
            ))}
            {/* Start balance reference */}
            <line x1="0" y1={sy(START_BALANCE)} x2={W} y2={sy(START_BALANCE)} stroke="#ffffff" strokeOpacity="0.15" strokeDasharray="6 6" />
            {/* Area fill */}
            <motion.path d={areaPath} fill="url(#eq-area)" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, delay: 0.3 }} />
            {/* Line */}
            <motion.path d={linePath} fill="none" stroke="url(#eq-line)" strokeWidth="2.5" filter="url(#eq-glow)" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }} />
            {/* End dot */}
            <motion.circle cx={sx(points[points.length - 1].x)} cy={sy(points[points.length - 1].y)} r="6" fill={finalColor} initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 2.3 }}>
              <animate attributeName="r" values="6;9;6" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
            </motion.circle>
          </svg>
          <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between py-2 text-[10px] font-mono text-gray-600 pointer-events-none">
            <span>${yTicks[2].toLocaleString()}</span>
            <span>${yTicks[1].toLocaleString()}</span>
            <span>${yTicks[0].toLocaleString()}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 relative z-10">
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Peak Balance</p>
            <p className="text-base sm:text-lg font-black text-white tabular-nums">${peakY.toFixed(0)}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Current</p>
            <p className="text-base sm:text-lg font-black tabular-nums" style={{ color: finalColor }}>${running.toFixed(0)}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Trades</p>
            <p className="text-base sm:text-lg font-black text-white tabular-nums">{trades.length}</p>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// --- P/L Heatmap: GitHub-style daily P/L grid for last 90 days ---
const PnLHeatmap = () => {
  const { trades, loading } = useLiveTrades();
  if (loading) return null;

  const DAYS = 90;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const days = [];
  for (let i = DAYS - 1; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86400000);
    days.push({ date: d, pnl: 0 });
  }
  trades.forEach(t => {
    const d = new Date(t.closed_at);
    d.setUTCHours(0, 0, 0, 0);
    const dayIdx = days.findIndex(x => x.date.getTime() === d.getTime());
    if (dayIdx >= 0) days[dayIdx].pnl += parseResult(t.result);
  });

  const maxAbs = Math.max(1, ...days.map(d => Math.abs(d.pnl)));
  const colorFor = (pnl) => {
    if (pnl === 0) return 'bg-white/[0.03] border-white/5';
    const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
    const opacity = 0.2 + intensity * 0.7;
    if (pnl > 0) return `border-green-500/10`;
    return `border-red-500/10`;
  };
  const styleFor = (pnl) => {
    if (pnl === 0) return {};
    const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
    const opacity = 0.15 + intensity * 0.7;
    return { backgroundColor: pnl > 0 ? `rgba(16, 185, 129, ${opacity})` : `rgba(239, 68, 68, ${opacity})` };
  };

  // Group into weeks (columns of 7)
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  const totalPL = days.reduce((s, d) => s + d.pnl, 0);
  const positiveDays = days.filter(d => d.pnl > 0).length;
  const negativeDays = days.filter(d => d.pnl < 0).length;

  return (
    <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 relative">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white/5 border border-white/10 rounded-3xl sm:rounded-[40px] p-6 sm:p-10 md:p-14 relative overflow-hidden"
      >
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/5 border border-blue-500/10 text-blue-400 text-[10px] font-black uppercase tracking-widest mb-4">
              90-Day Activity
            </div>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter uppercase">Daily <span className="text-gray-500">P/L Map</span></h2>
            <p className="text-gray-500 text-sm font-medium mt-2">Each square is one trading day · hover for details</p>
          </div>
          <div className="flex gap-6">
            <div>
              <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Green Days</p>
              <p className="text-2xl font-black text-green-500 tabular-nums">{positiveDays}</p>
            </div>
            <div>
              <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Red Days</p>
              <p className="text-2xl font-black text-red-500 tabular-nums">{negativeDays}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto pb-2">
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-1 flex-shrink-0">
              {week.map((day, di) => (
                <div
                  key={di}
                  className={`w-4 h-4 sm:w-5 sm:h-5 rounded-[3px] border ${colorFor(day.pnl)} transition-all hover:scale-125 hover:z-10 relative group cursor-default`}
                  style={styleFor(day.pnl)}
                  title={`${day.date.toLocaleDateString()}: ${day.pnl >= 0 ? '+' : ''}$${day.pnl.toFixed(0)}`}
                />
              ))}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-6 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
          <span>90 days ago</span>
          <div className="flex items-center gap-2">
            <span>Loss</span>
            <div className="flex gap-0.5">
              {[0.7, 0.5, 0.3, 0.15].map(o => <div key={o} className="w-3 h-3 rounded-sm" style={{ backgroundColor: `rgba(239, 68, 68, ${o})` }} />)}
              <div className="w-3 h-3 rounded-sm bg-white/[0.03] border border-white/5" />
              {[0.15, 0.3, 0.5, 0.7].map(o => <div key={o} className="w-3 h-3 rounded-sm" style={{ backgroundColor: `rgba(16, 185, 129, ${o})` }} />)}
            </div>
            <span>Profit</span>
          </div>
          <span>Today</span>
        </div>
      </motion.div>
    </section>
  );
};

// --- "What If" Calculator: simulate returns with custom starting capital ---
const WhatIfCalculator = () => {
  const { trades, account, loading } = useLiveTrades();
  const [capital, setCapital] = useState(10000);
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);

  if (loading || trades.length === 0) return null;

  // Adjust each trade for commission/swap so master simulation matches reality.
  const tradeSum = trades.reduce((s, t) => s + parseResult(t.result), 0);
  const realPL = account?.equity != null ? account.equity - START_BALANCE : tradeSum;
  const perTradeCost = (tradeSum - realPL) / trades.length;

  // Each trade is a % move on master starting balance; compound on user capital.
  let running = capital;
  let peak = capital;
  let maxDDPct = 0;
  const curve = [{ x: 0, y: capital, pl: 0, date: trades[0]?.closed_at }];
  const simulated = [];
  trades.forEach((t, i) => {
    const tradePctOfMaster = (parseResult(t.result) - perTradeCost) / START_BALANCE;
    const userPnl = running * tradePctOfMaster;
    simulated.push(userPnl);
    running += userPnl;
    if (running > peak) peak = running;
    const ddPct = peak > 0 ? ((peak - running) / peak) * 100 : 0;
    if (ddPct > maxDDPct) maxDDPct = ddPct;
    curve.push({ x: i + 1, y: running, pl: running - capital, date: t.closed_at });
  });
  const finalBalance = running;
  const wins = simulated.filter(x => x > 0).length;
  const winRate = (wins / simulated.length) * 100;
  const returnPct = ((finalBalance - capital) / capital) * 100;

  const minY = Math.min(...curve.map(p => p.y));
  const maxY = Math.max(...curve.map(p => p.y));
  const padY = (maxY - minY) * 0.1 || 100;
  const yLow = minY - padY, yHigh = maxY + padY;
  const W = 800, H = 220;
  const sx = (x) => (x / (curve.length - 1)) * W;
  const sy = (y) => H - ((y - yLow) / (yHigh - yLow)) * H;
  const path = curve.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.x).toFixed(1)} ${sy(p.y).toFixed(1)}`).join(' ');
  const color = returnPct >= 0 ? '#10b981' : '#ef4444';

  const handleMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round((x / W) * (curve.length - 1));
    setHoverIdx(Math.max(0, Math.min(curve.length - 1, idx)));
  };
  const handleLeave = () => setHoverIdx(null);

  const presets = [1000, 5000, 10000, 25000, 100000];
  const fmtDate = (ms) => ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  const hover = hoverIdx != null ? curve[hoverIdx] : null;

  return (
    <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 relative max-w-6xl">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/[0.04] to-transparent pointer-events-none" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[9px] font-black uppercase tracking-widest mb-2">
            <Sparkles size={10} /> Simulator
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase mb-2">What If <span className="text-gray-500">You'd Copied?</span></h2>
          <p className="text-gray-500 text-xs sm:text-sm font-medium mb-6">Real trades since {new Date(LIVE_START_MS).toLocaleDateString()} · adjusted for broker commission · compounded on your capital</p>

          <div className="grid lg:grid-cols-5 gap-5 sm:gap-6">
            <div className="lg:col-span-2 flex flex-col gap-4">
              {/* Capital slider */}
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Starting capital</label>
                  <span className="text-2xl sm:text-3xl font-black text-white tabular-nums">${capital.toLocaleString()}</span>
                </div>
                <input
                  type="range"
                  min="500"
                  max="100000"
                  step="500"
                  value={capital}
                  onChange={(e) => { setCapital(Number(e.target.value)); playSound('hover'); }}
                  className="w-full accent-blue-500"
                />
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {presets.map(v => (
                    <button
                      key={v}
                      onClick={() => { setCapital(v); playSound('click'); }}
                      className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all ${capital === v ? 'bg-blue-600 text-white' : 'bg-white/5 hover:bg-white/10 text-gray-400 border border-white/10'}`}
                    >
                      ${v >= 1000 ? `${v / 1000}k` : v}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Final</p>
                  <p className="text-base sm:text-lg font-black tabular-nums" style={{ color }}>${finalBalance.toFixed(0)}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Return</p>
                  <p className="text-base sm:text-lg font-black tabular-nums" style={{ color }}>{returnPct >= 0 ? '+' : ''}{returnPct.toFixed(1)}%</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Win Rate</p>
                  <p className="text-base sm:text-lg font-black text-white tabular-nums">{winRate.toFixed(1)}%</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Max DD</p>
                  <p className="text-base sm:text-lg font-black text-red-500 tabular-nums">{maxDDPct.toFixed(1)}%</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 bg-black/30 border border-white/10 rounded-xl p-3 sm:p-4 relative">
              <svg
                ref={svgRef}
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                className="w-full h-[200px] sm:h-[240px] cursor-crosshair"
                onMouseMove={handleMove}
                onMouseLeave={handleLeave}
                onTouchMove={(e) => { const t = e.touches[0]; handleMove({ clientX: t.clientX }); }}
                onTouchEnd={handleLeave}
              >
                <defs>
                  <linearGradient id="sim-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                  </linearGradient>
                </defs>
                <line x1="0" y1={sy(capital)} x2={W} y2={sy(capital)} stroke="#ffffff" strokeOpacity="0.15" strokeDasharray="4 4" />
                <path d={`${path} L ${W} ${H} L 0 ${H} Z`} fill="url(#sim-area)" />
                <path d={path} fill="none" stroke={color} strokeWidth="2" />
                {hover && (
                  <>
                    <line x1={sx(hover.x)} y1="0" x2={sx(hover.x)} y2={H} stroke="#ffffff" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="3 3" />
                    <circle cx={sx(hover.x)} cy={sy(hover.y)} r="5" fill={color} stroke="#ffffff" strokeWidth="2" />
                  </>
                )}
                {!hover && (
                  <circle cx={sx(curve.length - 1)} cy={sy(curve[curve.length - 1].y)} r="4" fill={color}>
                    <animate attributeName="r" values="4;7;4" dur="2s" repeatCount="indefinite" />
                  </circle>
                )}
              </svg>
              {hover && (
                <div
                  className="absolute pointer-events-none bg-black/90 border border-white/10 rounded-lg px-3 py-2 text-[10px] font-bold backdrop-blur-md shadow-xl"
                  style={{
                    left: `${(sx(hover.x) / W) * 100}%`,
                    top: '8px',
                    transform: `translateX(${hoverIdx > curve.length / 2 ? '-100%' : '0%'}) translateX(${hoverIdx > curve.length / 2 ? '-12px' : '12px'})`,
                  }}
                >
                  <div className="text-gray-500 uppercase tracking-widest text-[8px] mb-1">
                    {hoverIdx === 0 ? 'Start' : `Trade #${hoverIdx} · ${fmtDate(hover.date)}`}
                  </div>
                  <div className="text-white tabular-nums">${hover.y.toFixed(0)}</div>
                  <div className="tabular-nums" style={{ color: hover.pl >= 0 ? '#10b981' : '#ef4444' }}>
                    {hover.pl >= 0 ? '+' : ''}${hover.pl.toFixed(0)} ({hover.pl >= 0 ? '+' : ''}{((hover.pl / capital) * 100).toFixed(1)}%)
                  </div>
                </div>
              )}
              <p className="text-center text-[9px] font-bold text-gray-500 uppercase tracking-widest mt-1">{trades.length} trades · hover for details</p>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// --- Trading Report (daily or weekly — mirrors the template image) ---
const ReportPage = ({ period = 'week' }) => {
  const { trades, account, loading } = useLiveTrades();
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#050505',
      });
      const link = document.createElement('a');
      const datePart = new Date().toISOString().slice(0, 10);
      link.download = `flexbot-${period}-report-${datePart}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('[Report download] failed:', err);
      alert('Could not generate image. Try again or use a screenshot.');
    } finally {
      setDownloading(false);
    }
  };

  const now = new Date();
  let rangeStart, rangeEnd;
  if (period === 'day') {
    rangeStart = new Date(now);
    rangeStart.setUTCHours(0, 0, 0, 0);
    rangeEnd = new Date(rangeStart.getTime() + 86_400_000);
  } else {
    rangeStart = new Date(now);
    rangeStart.setUTCHours(0, 0, 0, 0);
    const day = rangeStart.getUTCDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    rangeStart.setUTCDate(rangeStart.getUTCDate() + diffToMon);
    rangeEnd = new Date(rangeStart.getTime() + 7 * 86_400_000);
  }

  const tradeSum = trades.reduce((s, t) => s + parseResult(t.result), 0);
  const realPL = account?.equity != null ? account.equity - START_BALANCE : tradeSum;
  const perTradeCost = trades.length > 0 ? (tradeSum - realPL) / trades.length : 0;

  const weekTrades = trades
    .filter(t => t.closed_at >= rangeStart.getTime() && t.closed_at < rangeEnd.getTime())
    .map(t => ({
      ...t,
      _r: parseResult(t.result) - perTradeCost,
      _date: new Date(t.closed_at),
    }))
    .sort((a, b) => a.closed_at - b.closed_at);

  // Breakdown: by weekday (week) or by session (day)
  let dailyResults;
  if (period === 'day') {
    const sessions = [
      { name: 'ASIA', range: [0, 6] },
      { name: 'LONDON', range: [6, 12] },
      { name: 'NY AM', range: [12, 18] },
      { name: 'NY PM', range: [18, 24] },
    ];
    dailyResults = sessions.map(s => {
      const sessionTrades = weekTrades.filter(t => {
        const h = t._date.getUTCHours();
        return h >= s.range[0] && h < s.range[1];
      });
      return {
        name: s.name,
        count: sessionTrades.length,
        pl: sessionTrades.reduce((sum, t) => sum + t._r, 0),
      };
    });
  } else {
    const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    dailyResults = dayNames.map((name, i) => {
      const dayDate = new Date(rangeStart.getTime() + i * 86_400_000);
      const dayTrades = weekTrades.filter(t => {
        const d = t._date;
        return d.getUTCFullYear() === dayDate.getUTCFullYear()
          && d.getUTCMonth() === dayDate.getUTCMonth()
          && d.getUTCDate() === dayDate.getUTCDate();
      });
      return {
        name,
        date: dayDate,
        count: dayTrades.length,
        pl: dayTrades.reduce((s, t) => s + t._r, 0),
      };
    });
  }

  // Cumulative P/L path across the week — drives mini equity curve + sparklines
  const equityPath = (() => {
    let running = 0;
    const pts = [{ x: 0, y: 0 }];
    weekTrades.forEach((t, i) => {
      running += t._r;
      pts.push({ x: i + 1, y: running });
    });
    return pts;
  })();
  const peakWeek = Math.max(0, ...equityPath.map(p => p.y));
  const troughWeek = Math.min(0, ...equityPath.map(p => p.y));
  const bestTradeIdx = weekTrades.reduce((bi, t, i) => weekTrades[bi]?._r > t._r ? bi : i, 0);
  const worstTradeIdx = weekTrades.reduce((wi, t, i) => weekTrades[wi]?._r < t._r ? wi : i, 0);

  // Build a small sparkline path from an array of {x,y} points within a box.
  const pathFromPoints = (pts, W, H, pad = 2) => {
    if (pts.length < 2) return `M 0 ${H / 2} L ${W} ${H / 2}`;
    const min = Math.min(...pts.map(p => p.y));
    const max = Math.max(...pts.map(p => p.y));
    const range = max - min || 1;
    const xmax = pts.length - 1;
    return pts.map((p, i) => {
      const x = (i / xmax) * (W - pad * 2) + pad;
      const y = H - pad - ((p.y - min) / range) * (H - pad * 2);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
  };

  const wins = weekTrades.filter(t => t._r > 0);
  const losses = weekTrades.filter(t => t._r < 0);
  const grossProfit = wins.reduce((s, t) => s + t._r, 0);
  const grossLoss = losses.reduce((s, t) => s + t._r, 0);
  const netResult = grossProfit + grossLoss;
  const winRate = weekTrades.length > 0 ? (wins.length / weekTrades.length) * 100 : 0;
  const bestTrade = weekTrades.length > 0 ? weekTrades.reduce((b, t) => t._r > b._r ? t : b, weekTrades[0]) : null;
  const worstTrade = weekTrades.length > 0 ? weekTrades.reduce((w, t) => t._r < w._r ? t : w, weekTrades[0]) : null;

  // Estimated broker cost split
  const commission = -Math.abs(perTradeCost * weekTrades.length * 0.85);
  const swap = -Math.abs(perTradeCost * weekTrades.length * 0.15);
  const netBalance = netResult;

  // Mini sparkline path
  const sparkline = (pts, dir) => {
    const W = 80, H = 32;
    if (pts.length < 2) return `M 0 ${H / 2} L ${W} ${H / 2}`;
    const min = Math.min(...pts), max = Math.max(...pts);
    const range = max - min || 1;
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i / (pts.length - 1) * W).toFixed(1)} ${(H - ((p - min) / range) * H).toFixed(1)}`).join(' ');
  };
  const upPts = [10, 12, 15, 14, 18, 22, 28, 35, 40];
  const downPts = [40, 35, 32, 30, 26, 22, 18, 14, 10];

  // Win-rate donut
  const radius = 30;
  const circ = 2 * Math.PI * radius;
  const winOffset = circ * (1 - winRate / 100);

  // Period-aware copy
  const periodLabel = period === 'day' ? 'today' : 'this week';
  const periodTitle = period === 'day' ? 'DAILY TRADING REPORT' : 'WEEKLY TRADING REPORT';
  const periodSubtitle = period === 'day' ? 'DAY OVERVIEW & RESULTS' : 'WEEK OVERVIEW & RESULTS';
  const breakdownTitle = period === 'day' ? 'Results by Session' : 'Results by Day';
  const breakdownLabel = period === 'day' ? 'SESSION' : 'DAY';

  // Summary bullets
  const bestSlot = dailyResults.reduce((b, d) => d.pl > (b?.pl || -Infinity) ? d : b, null);
  const summaryLines = weekTrades.length === 0
    ? [`Quiet ${periodLabel} — no closed trades yet`, 'Bot is in standby mode', `Check back at ${period === 'day' ? 'day' : 'week'} end`]
    : [
        netResult >= 0 ? `Net profit of $${netResult.toFixed(0)} ${periodLabel}` : `Net loss of $${Math.abs(netResult).toFixed(0)} — tough ${period === 'day' ? 'session' : 'period'}, reviewing setups`,
        bestSlot && bestSlot.count > 0 ? `Strongest ${period === 'day' ? 'session' : 'day'}: ${bestSlot.name} with ${bestSlot.count} trade${bestSlot.count > 1 ? 's' : ''}` : `Trades spread evenly across ${periodLabel}`,
        winRate >= 60 ? `Strong win rate of ${winRate.toFixed(0)}%` : winRate >= 40 ? `Balanced ${winRate.toFixed(0)}% win rate, R:R doing the work` : `Tight discipline despite ${winRate.toFixed(0)}% win rate`,
        'FTMO compliant risk management',
      ];

  const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

  return (
    <div className="relative min-h-screen py-8 sm:py-16 px-4 sm:px-6">
      <SEO
        title={`${period === 'day' ? 'Daily' : 'Weekly'} Trading Report — FlexBot AI`}
        description={`FlexBot AI ${period === 'day' ? 'daily' : 'weekly'} XAUUSD trading report. Live performance, ${period === 'day' ? 'session' : 'daily'} breakdown, win rate, and best/worst trades.`}
        path={period === 'day' ? '/daily-report' : '/weekly-report'}
      />
      <div className="mx-auto" style={{ maxWidth: 1080 }}>
        {/* Download bar (outside the captured card) */}
        <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
          <p className="text-[9px] font-bold text-gray-500 tracking-widest uppercase">
            Scroll sideways on mobile · download stays full-width
          </p>
          <button
            onClick={handleDownload}
            disabled={downloading || loading || trades.length === 0}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-white/5 disabled:text-gray-600 text-white px-4 py-2 rounded-xl font-black text-[10px] tracking-widest uppercase transition-all shadow-lg disabled:shadow-none disabled:cursor-not-allowed"
          >
            <Download size={14} />
            {downloading ? 'Generating…' : 'Download PNG'}
          </button>
        </div>

        {/* Horizontal scroll on small viewports — keeps locked-desktop layout */}
        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 pb-2">
          {/* Captured card — locked to desktop layout, no responsive variants */}
          <div ref={cardRef} id="report-card" style={{ width: 1080 }} className="bg-gradient-to-b from-[#0a1a3a] via-[#0a1530] to-[#050b1f] border border-cyan-500/30 rounded-3xl p-9 shadow-[0_0_60px_rgba(34,211,238,0.15)] relative overflow-hidden">
          <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{ background: 'radial-gradient(circle at top, rgba(34,211,238,0.05), transparent 60%)' }} />
          {/* Subtle dotted-grid pattern background */}
          <div
            className="absolute inset-0 rounded-3xl pointer-events-none opacity-[0.06]"
            style={{ backgroundImage: 'radial-gradient(rgba(34,211,238,0.6) 1px, transparent 1px)', backgroundSize: '24px 24px' }}
          />
          {/* Decorative corner brackets */}
          {[
            { top: 12, left: 12, rotate: 0 },
            { top: 12, right: 12, rotate: 90 },
            { bottom: 12, right: 12, rotate: 180 },
            { bottom: 12, left: 12, rotate: 270 },
          ].map((pos, i) => (
            <svg key={i} width="28" height="28" viewBox="0 0 28 28" className="absolute pointer-events-none" style={{ ...pos, transform: `rotate(${pos.rotate}deg)` }}>
              <path d="M 2 12 L 2 2 L 12 2" stroke="rgba(34,211,238,0.4)" strokeWidth="2" fill="none" strokeLinecap="round" />
            </svg>
          ))}

          {/* Header */}
          <div className="text-center mb-5 relative z-10">
            <div className="flex items-center justify-center gap-4 mb-1">
              <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-transparent to-cyan-400/50" />
              <h1 className="text-4xl font-black tracking-tight text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.3)' }}>
                {periodTitle}
              </h1>
              <div className="h-px flex-1 max-w-[100px] bg-gradient-to-l from-transparent to-cyan-400/50" />
            </div>
            <p className="text-green-400 text-base font-bold tracking-[0.3em]">{periodSubtitle}</p>
            <div className="flex items-center justify-center gap-3 mt-2 flex-wrap">
              <p className="text-cyan-300/60 text-xs font-bold tracking-widest">
                {period === 'day' ? fmtDate(rangeStart) : `${fmtDate(rangeStart)} — ${fmtDate(new Date(rangeEnd.getTime() - 86_400_000))}`} · XAUUSD
              </p>
              <span className="text-cyan-300/30">·</span>
              <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-[10px] font-black tracking-widest tabular-nums">
                {weekTrades.length} TRADES
              </span>
            </div>
          </div>

          {/* Mini equity path across the period */}
          {equityPath.length > 1 && (() => {
            const W = 1000, H = 64;
            const ys = equityPath.map(p => p.y);
            const yMin = Math.min(...ys, 0);
            const yMax = Math.max(...ys, 0);
            const yRange = (yMax - yMin) || 1;
            const sx = (i) => (i / (equityPath.length - 1)) * W;
            const sy = (y) => H - ((y - yMin) / yRange) * (H - 4) - 2;
            const linePath = equityPath.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(i).toFixed(1)} ${sy(p.y).toFixed(1)}`).join(' ');
            const areaPath = `${linePath} L ${sx(equityPath.length - 1).toFixed(1)} ${H} L 0 ${H} Z`;
            const zeroY = sy(0);
            const lineColor = netResult >= 0 ? '#4ade80' : '#f87171';
            return (
              <div className="relative z-10 mb-5 px-1">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[9px] font-black text-cyan-300/60 tracking-widest uppercase">Equity Path</p>
                  <p className="text-[9px] font-black text-cyan-300/60 tracking-widest tabular-nums">
                    Peak <span className="text-green-400">+${peakWeek.toFixed(0)}</span> · Trough <span className="text-red-400">${troughWeek.toFixed(0)}</span>
                  </p>
                </div>
                <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-14">
                  <defs>
                    <linearGradient id={`eq-week-${period}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={lineColor} stopOpacity="0.3" />
                      <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="rgba(34,211,238,0.25)" strokeWidth="1" strokeDasharray="4 4" />
                  <path d={areaPath} fill={`url(#eq-week-${period})`} />
                  <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2.5" style={{ filter: `drop-shadow(0 0 4px ${lineColor})` }} />
                  <circle cx={sx(equityPath.length - 1)} cy={sy(equityPath[equityPath.length - 1].y)} r="4" fill={lineColor} stroke="#050b1f" strokeWidth="2" />
                </svg>
              </div>
            );
          })()}

          {/* Top row: Results by Day + Total Result */}
          <div className="grid grid-cols-2 gap-4 mb-4 relative z-10">
            {/* Results by Day */}
            <div className="border-2 border-cyan-500/40 rounded-2xl p-4 bg-black/20">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                  <Activity size={14} className="text-cyan-400" />
                </div>
                <h2 className="text-sm font-black text-white tracking-wider uppercase">{breakdownTitle}</h2>
              </div>
              <div className="text-[10px] font-black text-gray-500 tracking-widest grid grid-cols-[60px_60px_1fr_80px] gap-2 mb-2 pb-2 border-b border-cyan-500/20">
                <span>{breakdownLabel}</span>
                <span className="text-center">TRADES</span>
                <span></span>
                <span className="text-right">RESULT</span>
              </div>
              {(() => {
                const maxAbs = Math.max(1, ...dailyResults.map(d => Math.abs(d.pl)));
                return dailyResults.map(d => {
                  const width = (Math.abs(d.pl) / maxAbs) * 100;
                  return (
                    <div key={d.name} className="grid grid-cols-[60px_60px_1fr_80px] gap-2 py-1.5 text-xs font-bold tabular-nums items-center">
                      <span className="text-gray-300">{d.name}</span>
                      <span className="text-center text-gray-300">{d.count}</span>
                      <div className="h-1 bg-white/5 rounded-full overflow-hidden relative">
                        <div className="h-1/2 absolute top-1/2 left-1/2 -translate-y-1/2 origin-left bg-gradient-to-r from-cyan-500/0 via-cyan-500/50 to-transparent" style={{ width: '1px' }} />
                        {d.pl !== 0 && (
                          <div
                            className={`h-full absolute top-0 ${d.pl > 0 ? 'left-1/2 bg-gradient-to-r from-green-500/60 to-green-400' : 'right-1/2 bg-gradient-to-l from-red-500/60 to-red-400'}`}
                            style={{ width: `${width / 2}%` }}
                          />
                        )}
                      </div>
                      <span className={`text-right ${d.pl > 0 ? 'text-green-400' : d.pl < 0 ? 'text-red-400' : 'text-gray-600'}`}>
                        {d.count === 0 ? '—' : `${d.pl >= 0 ? '+' : ''}$${d.pl.toFixed(0)}`}
                      </span>
                    </div>
                  );
                });
              })()}
              <div className="grid grid-cols-[60px_60px_1fr_80px] gap-2 pt-2 mt-2 border-t border-cyan-500/30 text-xs font-black tabular-nums items-center">
                <span className="text-white">TOTAL</span>
                <span className="text-center text-white">{weekTrades.length}</span>
                <span></span>
                <span className={`text-right ${netResult >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {netResult >= 0 ? '+' : ''}${netResult.toFixed(0)}
                </span>
              </div>
            </div>

            {/* Total Result */}
            <div className="border-2 border-cyan-500/40 rounded-2xl p-4 bg-black/20">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                  <TrendingUp size={14} className="text-green-400" />
                </div>
                <h2 className="text-sm font-black text-white tracking-wider uppercase">Total Result</h2>
              </div>
              <div className="text-center my-3 relative">
                {/* Glow halo behind the big number */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(ellipse 60% 70% at center, ${netResult >= 0 ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'}, transparent 70%)`,
                    filter: 'blur(8px)',
                  }}
                />
                <p className="text-[10px] font-black text-cyan-300/70 tracking-widest mb-1 relative">NET P/L</p>
                <p className={`text-6xl font-black tabular-nums relative ${netResult >= 0 ? 'text-green-400' : 'text-red-400'}`} style={{ textShadow: `0 0 30px ${netResult >= 0 ? 'rgba(74,222,128,0.6)' : 'rgba(248,113,113,0.6)'}, 0 0 60px ${netResult >= 0 ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)'}` }}>
                  {netResult >= 0 ? '+' : '-'}${Math.abs(netResult).toFixed(0)}
                </p>
                <p className="text-[10px] font-black text-white tracking-widest relative">USD</p>
              </div>
              <div className="border-t border-cyan-500/20 pt-2 space-y-1.5 text-xs tabular-nums">
                <div className="flex justify-between font-bold">
                  <span className="text-gray-400">Gross Profit</span>
                  <span className="text-green-400">${grossProfit.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span className="text-gray-400">Swap</span>
                  <span className="text-red-400">{swap.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span className="text-gray-400">Commission</span>
                  <span className="text-red-400">{commission.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-black pt-1.5 border-t border-cyan-500/20 mt-1.5">
                  <span className="text-white">NET BALANCE</span>
                  <span className={netBalance >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {netBalance >= 0 ? '' : '-'}${Math.abs(netBalance).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* All Trades Overview */}
          <div className="border-2 border-cyan-500/40 rounded-2xl p-4 bg-black/20 mb-4 relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <Package size={14} className="text-cyan-400" />
              </div>
              <h2 className="text-sm font-black text-white tracking-wider uppercase">All Trades Overview</h2>
            </div>
            {weekTrades.length === 0 ? (
              <p className="text-center text-gray-500 text-xs font-bold py-6">No trades closed this week yet</p>
            ) : (
              (() => {
                // At 1080px width, 3 columns fits nicely; 4 for very busy weeks.
                const colCount = weekTrades.length <= 18 ? 3 : 4;
                const rowsPerCol = Math.ceil(weekTrades.length / colCount);
                const cols = [];
                for (let i = 0; i < colCount; i++) {
                  cols.push(weekTrades.slice(i * rowsPerCol, (i + 1) * rowsPerCol));
                }
                const ROW = "grid grid-cols-[16px_1fr_50px_64px] gap-2 items-center";
                return (
                  <div className="grid gap-x-4 gap-y-0" style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
                    {cols.map((col, ci) => (
                      <div key={ci}>
                        <div className={`${ROW} text-[10px] font-black text-gray-500 tracking-widest pb-1.5 border-b border-cyan-500/20 mb-1`}>
                          <span></span>
                          <span>PAIR</span>
                          <span className="text-center">TYPE</span>
                          <span className="text-right">P/L</span>
                        </div>
                        {col.map((t, i) => (
                          <div key={i} className={`${ROW} py-1 text-[12px] font-bold tabular-nums`}>
                            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] leading-none ${t._r >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {t._r >= 0 ? '✓' : '✕'}
                            </span>
                            <span className="text-gray-200 tracking-wider">XAUUSD</span>
                            <span className={`text-center font-black text-[11px] tracking-wider ${t.direction === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>
                              {t.direction}
                            </span>
                            <span className={`text-right tabular-nums ${t._r >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {t._r >= 0 ? '+' : '-'}${Math.abs(t._r).toFixed(0)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })()
            )}
          </div>

          {/* Bottom row: compact Performance + Best/Worst + Summary */}
          <div className="grid grid-cols-3 gap-3 relative z-10">
            {/* Performance — donut + stats side-by-side */}
            <div className="border-2 border-cyan-500/40 rounded-2xl p-3 bg-black/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-md bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                  <Award size={11} className="text-yellow-400" />
                </div>
                <h2 className="text-[11px] font-black text-white tracking-wider uppercase">Performance</h2>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  <svg width="64" height="64" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r={radius} fill="none" stroke="rgba(239,68,68,0.3)" strokeWidth="7" />
                    <circle cx="40" cy="40" r={radius} fill="none" stroke="#4ade80" strokeWidth="7"
                      strokeDasharray={circ} strokeDashoffset={winOffset} strokeLinecap="round"
                      transform="rotate(-90 40 40)" style={{ filter: 'drop-shadow(0 0 4px rgba(74,222,128,0.6))' }} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[13px] font-black text-green-400 tabular-nums">{winRate.toFixed(0)}%</span>
                  </div>
                </div>
                <div className="flex-1 space-y-1 text-[10px] font-bold">
                  <div className="flex justify-between">
                    <span className="text-gray-400">WINS</span>
                    <span className="text-green-400 font-black tabular-nums">{wins.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">LOSSES</span>
                    <span className="text-red-400 font-black tabular-nums">{losses.length}</span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-cyan-500/20">
                    <span className="text-gray-400">TOTAL</span>
                    <span className="text-white font-black tabular-nums">{weekTrades.length}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Best/Worst Trade — tighter spacing, smaller dollar */}
            <div className="border-2 border-cyan-500/40 rounded-2xl p-3 bg-black/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-md bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                  <Sparkles size={11} className="text-green-400" />
                </div>
                <h2 className="text-[11px] font-black text-white tracking-wider uppercase">Best / Worst</h2>
              </div>
              {(() => {
                // Real sparkline: equity curve up to the best/worst trade index, highlighting it
                const W = 90, H = 36;
                const bestEnd = Math.min(bestTradeIdx + 2, equityPath.length - 1);
                const bestSlice = equityPath.slice(Math.max(0, bestTradeIdx - 4), bestEnd + 1);
                const worstEnd = Math.min(worstTradeIdx + 2, equityPath.length - 1);
                const worstSlice = equityPath.slice(Math.max(0, worstTradeIdx - 4), worstEnd + 1);
                return (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[9px] font-black text-green-400/70 tracking-widest uppercase">Best</p>
                        <p className="text-lg font-black text-green-400 tabular-nums leading-tight" style={{ textShadow: '0 0 8px rgba(74,222,128,0.5)' }}>
                          {bestTrade ? `+$${bestTrade._r.toFixed(0)}` : '—'}
                        </p>
                      </div>
                      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="flex-shrink-0">
                        <defs>
                          <linearGradient id={`up-fill-${period}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4ade80" stopOpacity="0.5" />
                            <stop offset="100%" stopColor="#4ade80" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <path d={`${pathFromPoints(bestSlice, W, H)} L ${W} ${H} L 0 ${H} Z`} fill={`url(#up-fill-${period})`} />
                        <path d={pathFromPoints(bestSlice, W, H)} fill="none" stroke="#4ade80" strokeWidth="2" style={{ filter: 'drop-shadow(0 0 3px #4ade80)' }} />
                      </svg>
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-2 mt-2 border-t border-cyan-500/20">
                      <div className="min-w-0">
                        <p className="text-[9px] font-black text-red-400/70 tracking-widest uppercase">Worst</p>
                        <p className="text-lg font-black text-red-400 tabular-nums leading-tight" style={{ textShadow: '0 0 8px rgba(248,113,113,0.5)' }}>
                          {worstTrade ? `-$${Math.abs(worstTrade._r).toFixed(0)}` : '—'}
                        </p>
                      </div>
                      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="flex-shrink-0">
                        <defs>
                          <linearGradient id={`dn-fill-${period}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f87171" stopOpacity="0.5" />
                            <stop offset="100%" stopColor="#f87171" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <path d={`${pathFromPoints(worstSlice, W, H)} L ${W} ${H} L 0 ${H} Z`} fill={`url(#dn-fill-${period})`} />
                        <path d={pathFromPoints(worstSlice, W, H)} fill="none" stroke="#f87171" strokeWidth="2" style={{ filter: 'drop-shadow(0 0 3px #f87171)' }} />
                      </svg>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Summary — tight bullets */}
            <div className="border-2 border-cyan-500/40 rounded-2xl p-3 bg-black/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-md bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                  <Check size={11} className="text-cyan-400" />
                </div>
                <h2 className="text-[11px] font-black text-white tracking-wider uppercase">Summary</h2>
              </div>
              <ul className="space-y-1">
                {summaryLines.slice(0, 4).map((line, i) => (
                  <li key={i} className="flex gap-1.5 text-[10px] font-bold leading-tight">
                    <span className="text-green-400 flex-shrink-0">✓</span>
                    <span className="text-gray-300">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-center gap-3 mt-6 pt-4 border-t border-cyan-500/20 relative z-10">
            <span className="text-cyan-400 text-xl">🚀</span>
            <p className="text-center text-sm font-black tracking-wider">
              <span className="text-white">FOCUS</span>
              <span className="text-cyan-400 mx-2">—</span>
              <span className="text-white">DISCIPLINE</span>
              <span className="text-cyan-400 mx-2">—</span>
              <span className="text-white">CONSISTENCY</span>
              <span className="text-cyan-400 mx-2">=</span>
              <span className="text-green-400">SUCCESS</span>
            </p>
          </div>
          <div className="flex items-center justify-between mt-1 relative z-10 px-1">
            <p className="text-[10px] font-bold text-cyan-300/40 tracking-[0.3em]">
              KEEP BUILDING. THE RESULTS WILL FOLLOW!
            </p>
            <p className="text-[10px] font-black text-cyan-300/40 tracking-[0.3em]">
              FLEXBOT.AI
            </p>
          </div>
        </div>
        </div>{/* close horizontal-scroll wrapper */}

        {/* Optional: link back */}
        <div className="text-center mt-6">
          <Link to="/results" className="text-[10px] font-black text-gray-500 hover:text-white tracking-widest uppercase transition-colors">← Back to live results</Link>
        </div>
      </div>

      {loading && (
        <div className="text-center mt-4 text-gray-500 text-xs font-bold">Loading live data…</div>
      )}
    </div>
  );
};

// --- Sound Toggle Button ---
const SoundToggle = () => {
  const [enabled, setEnabled] = useState(() => typeof window !== 'undefined' && localStorage.getItem(SOUND_KEY) === '1');
  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(SOUND_KEY, next ? '1' : '0');
    if (next) playSound('click');
  };
  return (
    <button
      onClick={toggle}
      title={enabled ? 'Sound on' : 'Sound off'}
      className="fixed bottom-5 right-5 z-50 w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={enabled ? 'text-blue-400' : 'text-gray-500'}>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
        {enabled ? (
          <>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          </>
        ) : (
          <>
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </>
        )}
      </svg>
    </button>
  );
};

// --- Shared Components ---
const TelegramSection = () => (
  <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-32 relative overflow-hidden">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-blue-600/[0.03] blur-[120px] rounded-full -z-10"></div>

    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1, ease: "easeOut" }}
      className="max-w-5xl mx-auto bg-white/5 border border-white/10 rounded-3xl sm:rounded-[48px] p-6 sm:p-12 md:p-20 relative overflow-hidden group shadow-2xl"
    >
      <div className="absolute top-0 right-0 p-12 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity duration-700 pointer-events-none">
        <Zap size={300} className="text-blue-500 rotate-12" />
      </div>

      <div className="relative z-10 flex flex-col items-center text-center">
        <div className="w-20 h-20 bg-blue-600/10 rounded-3xl flex items-center justify-center text-blue-500 mb-8 border border-blue-500/20 shadow-[0_0_30px_rgba(37,99,235,0.2)]">
          <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.69-.88-.48-1.38-.78-2.23-1.07-1-.32-.35-.5-.22-.82.04-.08.7-.64 1.28-1.22.74-.74 1.41-1.72 1.41-1.72.01-.01.01-.1-.05-.12-.06-.02-.15-.01-.15-.01s-.94.51-2.66 1.5c-.25.14-.48.21-.69.2-.23-.01-.67-.12-1-.25-.4-.16-.72-.24-.69-.51.02-.14.3-.28.84-.43 1.75-.64 2.92-1.07 3.51-1.28 1.67-.6 3.14-.7 4.05-.7.2 0 .64.04.93.12.24.07.47.22.54.43.07.21.08.45.07.67z"/>
          </svg>
        </div>

        <span className="text-blue-400 text-xs font-black uppercase tracking-[0.2em] sm:tracking-[0.4em] mb-4">Join Us</span>
        <h2 className="text-2xl sm:text-4xl md:text-6xl font-black text-white tracking-tighter mb-4 sm:mb-6 uppercase leading-tight">
          Telegram <span className="text-gray-500">Community</span>
        </h2>
        <p className="text-gray-400 text-sm sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-12 font-medium leading-relaxed">
          Join other FlexBot users. Get <span className="text-white">live trade updates</span>, ask questions, share results, and get direct support from our team.
        </p>

        <div className="flex flex-col sm:flex-row gap-6">
          <a 
            href="https://t.me/flexbotcommunity" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-blue-600 hover:bg-blue-500 text-white px-8 sm:px-12 py-4 sm:py-6 rounded-2xl font-black text-base sm:text-lg tracking-widest transition-all shadow-[0_10px_50px_rgba(37,99,235,0.4)] flex items-center gap-3 active:scale-95"
          >
            JOIN TELEGRAM <ArrowUpRight size={20} />
          </a>
          <div className="flex items-center gap-4 px-4 sm:px-8 py-4 sm:py-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
            <div className="flex -space-x-3">
              {['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-yellow-500'].map((color, i) => (
                <div key={i} className={`w-8 h-8 rounded-full border-2 border-[#050505] ${color} flex items-center justify-center text-white text-[10px] font-black`}>
                  {['F', 'B', 'A', 'I'][i]}
                </div>
              ))}
            </div>
            <div className="text-left">
              <p className="text-[10px] font-black text-white uppercase leading-none">Active Community</p>
              <div className="text-[8px] font-bold text-green-500 uppercase tracking-widest mt-1.5 flex items-center gap-1.5">
                <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div> Check inside
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  </section>
);

const RiskDisclaimer = () => (
  <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-24 border-t border-white/5">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center text-red-500 border border-red-500/20 shadow-[0_0_20px_rgba(239,68,68,0.1)]">
          <AlertTriangle size={20} />
        </div>
        <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Risk Warning & Disclaimer</h2>
      </div>
      
      <div className="grid md:grid-cols-2 gap-10 text-[11px] text-gray-500 font-bold leading-relaxed tracking-tight">
        <div className="space-y-6">
          <p>
            <span className="text-white uppercase tracking-widest text-[9px] block mb-1">High Risk Investment</span>
            Trading Gold (XAU/USD) and other financial instruments involves significant risk of loss and is not suitable for every investor. The high degree of leverage that is often under current trading conditions can work against you as well as for you.
          </p>
          <p>
            <span className="text-white uppercase tracking-widest text-[9px] block mb-1">No Financial Advice</span>
            FlexBot AI V5.0 is an automated software protocol designed for informational purposes only. Any signals or trades displayed are simulations and do not constitute financial, investment, or legal advice.
          </p>
        </div>
        <div className="space-y-6">
          <p>
            <span className="text-white uppercase tracking-widest text-[9px] block mb-1">Past Performance</span>
            Past performance is not indicative of future results. Hypothetical or simulated performance results have certain limitations. The protocol results shown (+342.18%) are based on audited master accounts and may vary significantly.
          </p>
          <p>
            <span className="text-white uppercase tracking-widest text-[9px] block mb-1">User Responsibility</span>
            By using FlexBot AI, you acknowledge that you are responsible for your own trading decisions. FlexBot AI and its developers shall not be held liable for any financial losses or damages resulting from the use of this software.
          </p>
        </div>
      </div>
      
      <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
          <p className="text-[9px] font-black text-gray-600 uppercase tracking-[0.3em]">Official Protocol V5.0.4</p>
        </div>
        <p className="text-[9px] font-bold text-gray-700 uppercase tracking-widest">Global Software Node: #FX-884210</p>
      </div>
    </div>
  </section>
);

const SectionDivider = () => (
  <motion.div 
    initial={{ opacity: 0, scaleX: 0 }}
    whileInView={{ opacity: 1, scaleX: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 1.5, ease: "circOut" }}
    className="relative h-px w-full max-w-5xl mx-auto my-4 opacity-50 origin-center"
  >
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-blue-500/10 blur-2xl rounded-full"></div>
  </motion.div>
);

const FeaturesSection = () => {
  const { trades } = useLiveTrades();
  const winRate = trades.length > 0
    ? (trades.filter(t => parseResult(t.result) > 0).length / trades.length) * 100
    : null;
  return (
    <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-32 relative">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="text-center mb-10 sm:mb-16"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/5 border border-blue-500/10 text-blue-500 text-[10px] font-black uppercase tracking-widest mb-6">
          Why FlexBot AI
        </div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-4 uppercase">Built For <span className="text-gray-500">Results</span></h2>
      </motion.div>

      {/* Top row: big feature + stat */}
      <div className="grid lg:grid-cols-5 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="lg:col-span-3 bg-white/[0.03] border border-white/10 rounded-2xl sm:rounded-[40px] p-6 sm:p-10 relative overflow-hidden group"
        >
          <div className="absolute top-0 right-0 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity pointer-events-none"><Shield size={280} /></div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center justify-center text-blue-500 mb-6">
              <Shield size={24} />
            </div>
            <h3 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-3">Your Money Never Leaves Your Broker</h3>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-xl">FlexBot connects to your MT5 via a secure bridge. We copy trades — we never touch your funds. You stay in full control of your account at all times.</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2 bg-blue-600 rounded-2xl sm:rounded-[40px] p-6 sm:p-10 relative overflow-hidden group flex flex-col justify-between"
        >
          <div className="absolute -right-6 -bottom-6 opacity-20 group-hover:scale-110 transition-transform"><TrendingUp size={140} className="text-white" /></div>
          <div className="relative z-10">
            <p className="text-[10px] font-black text-blue-100 uppercase tracking-widest mb-2 opacity-80">Verified Performance</p>
            <h3 className="text-5xl sm:text-6xl font-black text-white tracking-tighter mb-2 tabular-nums">{winRate != null ? `${winRate.toFixed(1)}%` : '—'}</h3>
            <p className="text-blue-100 font-bold text-sm">Live win rate from our master account. Every trade verifiable on the public results page.</p>
          </div>
        </motion.div>
      </div>

      {/* Bottom row: 3 features */}
      <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
        {[
          {
            icon: <Zap size={20} />,
            title: 'Instant Execution',
            desc: 'Trades are copied from our master account to yours in real-time. No delays, no requotes.',
            style: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-500'
          },
          {
            icon: <Activity size={20} />,
            title: 'Smart Risk Control',
            desc: 'Auto daily loss limits, news filters, and dynamic lot sizing. Built to protect prop firm accounts.',
            style: 'bg-purple-500/10 border-purple-500/20 text-purple-500'
          },
          {
            icon: <Clock size={20} />,
            title: 'Set & Forget',
            desc: 'Install once, runs 24/5. No charts to watch, no signals to follow. FlexBot handles everything.',
            style: 'bg-green-500/10 border-green-500/20 text-green-500'
          },
        ].map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 * i }}
            className="bg-white/[0.03] border border-white/10 rounded-2xl sm:rounded-[32px] p-6 sm:p-8 group hover:bg-white/[0.05] transition-all"
          >
            <div className={`w-10 h-10 ${f.style} border rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
              {f.icon}
            </div>
            <h3 className="text-lg font-black text-white mb-2 uppercase tracking-tight">{f.title}</h3>
            <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const PerformanceSection = () => {
  return (
    <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-32 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-600/[0.02] blur-[120px] rounded-full -z-10"></div>
      <motion.div
        initial={{ opacity: 0, y: 100, scale: 0.95 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white/5 border border-white/10 rounded-3xl sm:rounded-[48px] overflow-hidden shadow-2xl relative z-10"
      >
        <div className="grid lg:grid-cols-2">
          <div className="p-6 sm:p-12 md:p-20 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-white/10">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 1, ease: "easeOut" }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/5 border border-green-500/10 text-green-500 text-[10px] font-black uppercase tracking-widest mb-8 w-fit">
                FTMO Compliant
              </div>
              <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-6 sm:mb-8 uppercase">Prop Firm <span className="text-gray-500">Ready</span></h2>
              <p className="text-gray-400 text-lg font-medium leading-relaxed mb-10">
                FlexBot is explicitly engineered to pass and maintain strict prop firm challenges like FTMO. Our strict parameter protocol ensures absolute compliance.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 p-4 rounded-[20px] transition-colors hover:bg-white/10">
                  <PieChart size={24} className="text-blue-500 flex-shrink-0" />
                  <div>
                    <p className="text-white font-black text-sm uppercase">Max Risk: 0.5%</p>
                    <p className="text-gray-500 text-[9px] font-bold uppercase tracking-widest">Per Trade Strict Limit</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 p-4 rounded-[20px] transition-colors hover:bg-white/10">
                  <Activity size={24} className="text-purple-500 flex-shrink-0" />
                  <div>
                    <p className="text-white font-black text-sm uppercase">1 Trade Max</p>
                    <p className="text-gray-500 text-[9px] font-bold uppercase tracking-widest">Open At Any Time</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 p-4 rounded-[20px] transition-colors hover:bg-white/10">
                  <Clock size={24} className="text-yellow-500 flex-shrink-0" />
                  <div>
                    <p className="text-white font-black text-sm uppercase">No News Trading</p>
                    <p className="text-gray-500 text-[9px] font-bold uppercase tracking-widest">Avoids High Impact</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 p-4 rounded-[20px] transition-colors hover:bg-white/10">
                  <Shield size={24} className="text-green-500 flex-shrink-0" />
                  <div>
                    <p className="text-white font-black text-sm uppercase">Balanced Logic</p>
                    <p className="text-gray-500 text-[9px] font-bold uppercase tracking-widest">No One-Side Betting</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 1 }}
            className="h-full lg:h-auto relative overflow-hidden"
          >
            <img src="/ftmo-reward.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.25] z-0" style={{ objectPosition: 'center 30%' }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent z-[1]"></div>
            <div className="relative z-[2]">
              <ChallengeSteps />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "How does FlexBot AI work?",
      a: "FlexBot AI is a plug-and-play Expert Advisor for MetaTrader. It automatically copies trades from our Master Neural Account to your broker. You maintain 100% control of your funds."
    },
    {
      q: "Do I need to send money to FlexBot AI?",
      a: "No. You never send your trading capital to us. Your money stays in your own brokerage account. You only pay for the software license."
    },
    {
      q: "Which brokers are supported?",
      a: "FlexBot AI works with any broker supporting MetaTrader 5. We recommend low-spread brokers for gold (XAU/USD)."
    },
    {
      q: "What is the fixed price?",
      a: "The FlexBot AI lifetime license is available for a one-time payment of $500. This includes all future updates to the Neural V5.0 core and 24/7 technical support."
    },
    {
      q: "Are there any recurring fees?",
      a: "A $30/month subscription fee is required to maintain the AI connection and receive real-time neural updates on your account."
    }
  ];

  return (
    <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-32 relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-10 sm:mb-16"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/5 border border-blue-500/10 text-blue-500 text-[10px] font-black uppercase tracking-widest mb-6">
          Support Center
        </div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tighter uppercase mb-4 text-white">Frequently Asked <span className="text-gray-500">Questions</span></h2>
        <p className="text-gray-500 font-medium max-w-2xl mx-auto">Everything you need to know about the Flexbot protocol and our neural execution.</p>
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="max-w-3xl mx-auto space-y-4"
      >
        {faqs.map((faq, i) => (
          <div key={i} className="border border-white/5 rounded-2xl overflow-hidden bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
            <button
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="w-full px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between text-left"
            >
              <span className="font-bold text-white tracking-tight">{faq.q}</span>
              <div className={`transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`}>
                <ArrowUpRight size={18} className="text-blue-500" />
              </div>
            </button>
            <AnimatePresence>
              {openIndex === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="px-4 sm:px-8 pb-4 sm:pb-6 text-gray-400 text-sm leading-relaxed font-medium"
                >
                  {faq.a}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </motion.div>
    </section>
  );
};

const HowItWorks = () => {
  const steps = [
    {
      title: "Purchase License",
      desc: "Secure your FlexBot AI lifetime license for $500 via USDT (BEP-20) payment.",
      icon: <DollarSign size={24} />,
      color: "blue"
    },
    {
      title: "Install on MT5",
      desc: "Deploy the FlexBot AI software on your MetaTrader 5 terminal using our 5-minute guide.",
      icon: <Settings size={24} />,
      color: "yellow"
    },
    {
      title: "Connect AI Core",
      desc: "Link your account to the FlexBot AI Neural Stream for automatic institutional trade copying.",
      icon: <Activity size={24} />,
      color: "blue"
    },
    {
      title: "Full Autonomy",
      desc: "FlexBot AI executes everything. Watch your equity grow while maintaining 100% control.",
      icon: <Shield size={24} />,
      color: "green"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section id="how-it-works" className="container mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-32 relative">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.8 }}
        className="text-center mb-20"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/5 border border-blue-500/10 text-blue-500 text-[10px] font-black uppercase tracking-widest mb-6">
          The Process
        </div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-6 uppercase text-white">How It <span className="text-gray-500">Works</span></h2>
        <p className="text-gray-400 max-w-2xl mx-auto font-medium leading-relaxed">Follow these four steps to initialize institutional execution on your account.</p>
      </motion.div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 relative"
      >
        <motion.div 
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 1.5, ease: "circIn" }}
          className="hidden lg:block absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-y-1/2 z-0 origin-left"
        ></motion.div>
        
        {steps.map((step, i) => (
          <motion.div 
            key={i} 
            variants={stepVariants}
            className="relative z-10 flex flex-col items-center text-center group"
          >
            <div className={`w-20 h-20 rounded-3xl mb-8 flex items-center justify-center transition-all duration-500 border border-white/10 ${
              step.color === 'blue' ? 'bg-blue-600/10 text-blue-500 group-hover:bg-blue-600 group-hover:text-white group-hover:shadow-[0_0_30px_rgba(37,99,235,0.4)]' :
              step.color === 'green' ? 'bg-green-600/10 text-green-500 group-hover:bg-green-600 group-hover:text-white group-hover:shadow-[0_0_30px_rgba(34,197,94,0.4)]' :
              'bg-yellow-600/10 text-yellow-500 group-hover:bg-yellow-600 group-hover:text-white group-hover:shadow-[0_0_30px_rgba(234,179,8,0.4)]'
            }`}>
              {step.icon}
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white/5 border border-white/10 backdrop-blur-xl flex items-center justify-center text-[10px] font-black text-white">
                0{i + 1}
              </div>
            </div>
            <h3 className="text-lg font-black text-white mb-3 uppercase tracking-tight">{step.title}</h3>
            <p className="text-gray-500 text-sm font-medium leading-relaxed px-4">{step.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

const TradingViewWidget = () => {
  const container = useRef();
  const [widgetId] = useState("tv_" + Math.random().toString(36).substring(7));

  useEffect(() => {
    let isMounted = true;
    const currentContainer = container.current;

    const createWidget = () => {
      if (!isMounted || !currentContainer) return;
      currentContainer.innerHTML = '';
      
      const script = document.createElement("script");
      script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
      script.type = "text/javascript";
      script.async = true;
      script.innerHTML = JSON.stringify({
        "autosize": true,
        "symbol": "OANDA:XAUUSD",
        "interval": "1",
        "timezone": "Etc/UTC",
        "theme": "dark",
        "style": "1",
        "locale": "en",
        "enable_publishing": false,
        "allow_symbol_change": false,
        "container_id": widgetId,
        "backgroundColor": "rgba(0, 0, 0, 0)",
        "gridColor": "rgba(255, 255, 255, 0.05)",
        "hide_top_toolbar": true,
        "hide_legend": true,
        "save_image": false
      });
      
      currentContainer.appendChild(script);
    };

    const timer = setTimeout(createWidget, 500);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (currentContainer) currentContainer.innerHTML = '';
    };
  }, [widgetId]);

  return (
    <div className="tradingview-widget-container" style={{ height: "100%", width: "100%" }}>
      <div id={widgetId} ref={container} style={{ height: "100%", width: "100%" }}></div>
    </div>
  );
};

const BackgroundEffects = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
    <div className="absolute top-[-10%] left-[-10%] w-[800px] h-[800px] bg-blue-600/[0.03] blur-[150px] rounded-full animate-pulse"></div>
    <div className="absolute top-[30%] right-[-10%] w-[600px] h-[600px] bg-blue-500/[0.02] blur-[130px] rounded-full"></div>
    <div className="absolute bottom-[-10%] left-[5%] w-[700px] h-[700px] bg-blue-900/[0.03] blur-[140px] rounded-full"></div>
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505] opacity-50"></div>
  </div>
);

const Logo = ({ className = "" }) => (
  <div className={`flex items-center gap-2.5 md:gap-3.5 group cursor-pointer ${className}`}>
    <div className="relative">
      <div className="absolute inset-[-10px] bg-blue-500/20 blur-2xl rounded-full opacity-40 group-hover:opacity-100 transition-opacity duration-500"></div>
      <div className="absolute inset-[-4px] bg-gradient-to-br from-blue-500/30 to-purple-500/30 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      <div className="relative w-11 h-11 md:w-14 md:h-14 flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
        <img
          src="/FLEX.png"
          alt="FlexBot AI Logo"
          className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(59,130,246,0.45)] group-hover:drop-shadow-[0_0_25px_rgba(59,130,246,0.7)] transition-all duration-500"
        />
      </div>
    </div>
    <div className="flex flex-col">
      <div className="flex items-baseline">
        <span className="text-xl md:text-2xl font-black tracking-tighter bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent">FlexBot</span>
        <span className="text-xl md:text-2xl font-black tracking-tighter bg-gradient-to-br from-blue-400 to-blue-600 bg-clip-text text-transparent ml-0.5 md:ml-1 drop-shadow-[0_0_12px_rgba(59,130,246,0.5)]">AI</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-1 h-1 rounded-full bg-blue-500 animate-pulse shadow-[0_0_6px_rgba(59,130,246,0.8)]"></div>
        <span className="text-[7px] md:text-[8px] font-black text-blue-400/70 uppercase tracking-[0.3em] leading-none">Neural Protocol</span>
      </div>
    </div>
  </div>
);

const ChallengeSteps = () => (
  <div className="w-full h-full relative bg-transparent p-5 sm:p-8 md:p-12 flex flex-col justify-center overflow-hidden">
    <div className="absolute inset-0 opacity-[0.02] pointer-events-none" 
         style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
    </div>
    
    <div className="relative z-10 mb-10 flex items-end justify-between">
      <div>
        <p className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] mb-1">Execution Plan</p>
        <h4 className="text-2xl sm:text-3xl font-black tracking-tighter text-white uppercase">How We Pass You</h4>
      </div>
      <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-xl">
        <div className="w-2 h-2 rounded-full bg-green-500"></div>
        <span className="text-[8px] font-black text-green-500 uppercase tracking-widest">FTMO Verified</span>
      </div>
    </div>

    <div className="relative z-10 flex-1 flex flex-col gap-6">
      {/* Step 1 */}
      <div className="flex gap-6 relative group">
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-2xl bg-blue-600/10 border border-blue-500/30 flex items-center justify-center text-blue-400 font-black text-sm z-10 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-[0_0_20px_rgba(37,99,235,0.1)] group-hover:shadow-[0_0_20px_rgba(37,99,235,0.4)]">1</div>
          <div className="w-px h-full bg-gradient-to-b from-blue-500/30 to-purple-500/30 mt-3 absolute top-10 bottom-[-24px]"></div>
        </div>
        <div className="pb-4">
          <h5 className="text-white font-black uppercase text-sm mb-1.5 tracking-tight">Phase 1: Evaluation</h5>
          <p className="text-gray-500 text-xs font-medium leading-relaxed">Dynamic lot sizing to safely secure the 8-10% profit target while strictly avoiding the daily drawdown limits.</p>
        </div>
      </div>

      {/* Step 2 */}
      <div className="flex gap-6 relative group mt-4">
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-2xl bg-purple-600/10 border border-purple-500/30 flex items-center justify-center text-purple-400 font-black text-sm z-10 group-hover:bg-purple-600 group-hover:text-white transition-all shadow-[0_0_20px_rgba(147,51,234,0.1)] group-hover:shadow-[0_0_20px_rgba(147,51,234,0.4)]">2</div>
          <div className="w-px h-full bg-gradient-to-b from-purple-500/30 to-green-500/30 mt-3 absolute top-10 bottom-[-24px]"></div>
        </div>
        <div className="pb-4">
          <h5 className="text-white font-black uppercase text-sm mb-1.5 tracking-tight">Phase 2: Verification</h5>
          <p className="text-gray-500 text-xs font-medium leading-relaxed">Risk parameters are automatically halved. Pure focus on hitting the 5% target with zero stress.</p>
        </div>
      </div>

      {/* Step 3 */}
      <div className="flex gap-6 relative group mt-4">
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-2xl bg-green-600/10 border border-green-500/30 flex items-center justify-center text-green-400 font-black text-sm z-10 group-hover:bg-green-600 group-hover:text-white transition-all shadow-[0_0_20px_rgba(34,197,94,0.1)] group-hover:shadow-[0_0_20px_rgba(34,197,94,0.4)]">3</div>
        </div>
        <div>
          <h5 className="text-white font-black uppercase text-sm mb-1.5 tracking-tight">Funded & Payouts</h5>
          <p className="text-gray-500 text-xs font-medium leading-relaxed">Ultra-safe mode activated. The AI trades with maximum capital preservation to generate consistent bi-weekly payouts.</p>
        </div>
      </div>
    </div>
  </div>
);

const ProfessionalIcon = ({ type, color = "blue" }) => {
  const colorClass = 
    color === "green" ? "text-green-500" : 
    color === "purple" ? "text-purple-500" :
    color === "yellow" ? "text-yellow-500" :
    "text-blue-500";
    
  const bgClass = 
    color === "green" ? "bg-green-500/5 border-green-500/20" : 
    color === "purple" ? "bg-purple-500/5 border-purple-500/20" :
    color === "yellow" ? "bg-yellow-500/5 border-yellow-500/20" :
    "bg-blue-500/5 border-blue-500/20";
    
  const icons = {
    withdraw: <Clock size={20} className={colorClass} />,
    secure: <Shield size={20} className={colorClass} />,
    performance: <Activity size={20} className={colorClass} />,
    profit: <DollarSign size={20} className={colorClass} />
  };
  return <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${bgClass} border`}>{icons[type]}</div>;
};

// --- Navbar ---
const Navbar = ({ onBuyClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="flex justify-between items-center px-4 md:px-8 py-3 sm:py-4 border-b border-white/10 bg-[#050505]/80 backdrop-blur-2xl sticky top-0 z-50">
        <Link to="/"><Logo /></Link>
        {!isDashboard && (
          <div className="hidden lg:flex items-center gap-1 bg-white/5 border border-white/10 p-1.5 rounded-full backdrop-blur-xl shadow-inner">
            <Link to="/results" className="px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300">Live Results</Link>
            <div className="w-px h-4 bg-white/10"></div>
            <Link to="/daily-report" className="px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300">Daily</Link>
            <div className="w-px h-4 bg-white/10"></div>
            <Link to="/weekly-report" className="px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300">Weekly</Link>
            <div className="w-px h-4 bg-white/10"></div>
            <Link to="/leaderboard" className="px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300">Leaderboard</Link>
            <div className="w-px h-4 bg-white/10"></div>
            <Link to="/myfxbook" className="px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300">Myfxbook</Link>
            <div className="w-px h-4 bg-white/10"></div>
            <Link to="/how-it-works" className="px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300">How It Works</Link>
          </div>
        )}
        <div className="flex items-center gap-2 md:gap-4">
          {!isDashboard && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-400 hover:text-white transition-colors"
            >
              {mobileMenuOpen ? <X size={22} /> : <Settings size={22} />}
            </button>
          )}
          <Link to="/dashboard" className="group relative flex items-center gap-2 bg-blue-600/10 hover:bg-blue-600 border border-blue-500/20 hover:border-blue-400 px-3 md:px-5 py-2 md:py-2.5 rounded-full transition-all duration-300">
            <div className="flex items-center gap-2 relative z-10">
              <LayoutDashboard size={14} className="text-blue-500 group-hover:text-white transition-colors" />
              <span className="text-xs md:text-sm font-black tracking-widest text-white uppercase">Dashboard</span>
              <ArrowUpRight size={12} className="hidden md:block text-blue-500 group-hover:text-white transition-all" />
            </div>
          </Link>
        </div>
      </nav>
      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && !isDashboard && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden fixed top-[57px] left-0 right-0 z-40 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10 p-4"
          >
            <div className="flex flex-col gap-2">
              <Link to="/results" className="px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/5 transition-all">Live Results</Link>
              <Link to="/daily-report" className="px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/5 transition-all">Daily Report</Link>
              <Link to="/weekly-report" className="px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/5 transition-all">Weekly Report</Link>
              <Link to="/leaderboard" className="px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/5 transition-all">Leaderboard</Link>
              <Link to="/myfxbook" className="px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/5 transition-all">Myfxbook</Link>
              <Link to="/how-it-works" className="px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-white hover:bg-white/5 transition-all">How It Works</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// --- Page: Landing ---
const LandingPage = ({ onBuyClick, tradingLogs }) => {
  return (
    <div className="relative">
      <SEO
        title="FlexBot AI — Institutional Gold (XAUUSD) Trading EA for MT5"
        description="Institutional-grade gold trading AI for MT5. Audited live results, transparent master account, copy directly to your broker. Built for FTMO and prop-firm challenges."
        path="/"
      />
      <header className="relative w-full lg:h-[calc(100vh-60px)] flex flex-col items-center justify-center overflow-visible">
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-blue-900/5 to-transparent"></div>
          <div className="absolute top-[-20%] left-[-20%] w-[140%] h-[140%] bg-blue-600/[0.05] blur-[150px] rounded-full animate-pulse"></div>
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.04] mix-blend-overlay"></div>
          <svg className="absolute bottom-0 left-0 w-full h-[60%] opacity-20" viewBox="0 0 1440 400">
            <motion.path initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 3, ease: "easeInOut" }} d="M0,320 L120,280 L240,340 L360,200 L480,260 L600,140 L720,220 L840,80 L960,160 L1080,40 L1200,100 L1320,20 L1440,60" fill="none" stroke="url(#gradient-line)" strokeWidth="4" />
            <defs><linearGradient id="gradient-line" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#2563eb" /><stop offset="100%" stopColor="#06b6d4" /></linearGradient></defs>
          </svg>
          <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:py-0 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-6 max-w-7xl mx-auto">
            {/* Left Column - Text */}
            <motion.div
               initial={{ opacity: 0, x: -50 }}
               animate={{ opacity: 1, x: 0 }}
               transition={{ duration: 1 }}
               className="flex-1 text-center lg:text-left flex flex-col items-center lg:items-start"
            >
              <div className="inline-flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mb-3 lg:mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] lg:text-xs font-bold"><div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>V5.0 NEURAL NETWORK</div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-400 text-[10px] lg:text-xs font-bold uppercase tracking-widest leading-none"><Zap size={14} className="text-blue-500" />Gold Specialized</div>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[2.75rem] xl:text-5xl 2xl:text-7xl font-black mb-2 sm:mb-3 lg:mb-3 leading-[1.05] tracking-tight uppercase">Gold <br /><span className="text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]">FlexBot AI</span></h1>
              <p className="text-gray-400 text-sm sm:text-base lg:text-sm xl:text-base max-w-xl mx-auto lg:mx-0 mb-4 sm:mb-6 lg:mb-5 leading-relaxed font-medium">Get the <span className="text-white font-black">Flexbot Expert Advisor</span>. Institutional-grade gold trading software copied directly to <span className="text-white font-bold">your own broker account</span>.</p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start w-full sm:w-auto relative">
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 sm:left-auto sm:right-[-40px] sm:translate-x-0 rotate-12 z-20">
                  <div className="bg-blue-600 text-white text-[9px] font-black px-3 py-1 rounded-lg shadow-xl flex flex-col items-center border border-white/20">
                    <span>$500 LIFETIME</span>
                    <span className="text-[6px] mt-0.5 opacity-70">LIMITED SLOTS</span>
                  </div>
                </div>
                <button onClick={() => { playSound('click'); onBuyClick(); }} onMouseEnter={() => playSound('hover')} className="bg-blue-600 hover:bg-blue-500 text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-2xl font-black text-sm sm:text-base transition-all shadow-[0_10px_50px_rgba(37,99,235,0.4)] hover:-translate-y-1 group flex items-center justify-center gap-3">GET FLEXBOT AI <ArrowUpRight className="group-hover:translate-x-1 transition-transform" size={18} /></button>
                <a href="#how-it-works" className="bg-white/5 hover:bg-white/10 border border-white/10 px-6 sm:px-8 py-3 sm:py-3.5 rounded-2xl font-black text-sm sm:text-base transition-all backdrop-blur-md flex items-center justify-center">HOW IT WORKS</a>
              </div>
            </motion.div>

            {/* Right Column - Astronaut */}
            <motion.div
               initial={{ opacity: 0, x: 50 }}
               animate={{ opacity: 1, x: 0 }}
               transition={{ duration: 1, delay: 0.2 }}
               className="flex-1 relative w-full max-w-[320px] sm:max-w-[400px] lg:max-w-[380px] xl:max-w-[450px] 2xl:max-w-[550px] flex justify-center items-center pointer-events-none mt-4 lg:mt-0"
            >
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-600/10 blur-[130px] rounded-full -z-10 animate-pulse"></div>
               <motion.img
                  initial={{ y: 0 }}
                  animate={{ y: [-15, 15, -15] }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                  src="/photo_2026-04-04_12-16-05.jpg"
                  alt="FlexBot FTMO Astronaut"
                  className="w-full h-auto object-contain mix-blend-lighten contrast-[1.2] opacity-100 drop-shadow-2xl"
                  style={{ maskImage: 'radial-gradient(ellipse at center, black 45%, transparent 75%)', WebkitMaskImage: 'radial-gradient(ellipse at center, black 45%, transparent 75%)' }}
               />
            </motion.div>
          </div>
        </div>
      </header>
      
      {/* Decorative Section Divider */}
      <div className="relative h-px w-full max-w-6xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-blue-500/10 blur-xl rounded-full"></div>
      </div>
      
      <EquityCurveSection />
      <SectionDivider />

      <PerformanceSection />
      <SectionDivider />

      <WhatIfCalculator />
      <SectionDivider />

      <PnLHeatmap />
      <SectionDivider />

      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-600/[0.01] pointer-events-none"></div>
        <HowItWorks />
      </div>

      <SectionDivider />
      <FeaturesSection />

      <SectionDivider />
      <TelegramSection />

      <SectionDivider />
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-600/[0.01] pointer-events-none"></div>
        <FAQSection />
      </div>

      <RiskDisclaimer />
    </div>
  );
};

// --- Page: Dashboard ---
// --- Helpers for Real Analysis ---
const calculateSMA = (prices, period) => {
  if (prices.length < period) return null;
  return prices.slice(-period).reduce((a, b) => a + b, 0) / period;
};

const calculateRSI = (prices, period = 14) => {
  if (prices.length < period + 1) return 50;
  let gains = 0, losses = 0;
  for (let i = prices.length - period; i < prices.length; i++) {
    const diff = prices[i] - prices[i - 1];
    if (diff >= 0) gains += diff; else losses -= diff;
  }
  if (losses === 0) return 100;
  const rs = gains / losses;
  return 100 - (100 / (1 + rs));
};

  // --- Account History Chart ---
const AccountHistoryChart = ({ isLinked, dailyGrowth }) => {
  const [timeRange, setTimeRange] = useState('1W');

  // Simulated data for different time ranges
  const getChartData = () => {
    if (!isLinked) return Array(30).fill(50);
    
    // If we have real daily growth data, use it for 1W and 1M
    if (dailyGrowth && dailyGrowth.length > 0) {
      const growthPoints = dailyGrowth.map(d => d.profitPercentage || 0);
      
      // Calculate cumulative growth starting from 50 (middle of the chart)
      let cumulative = 50;
      const realPoints = growthPoints.map(p => {
        cumulative += p;
        return cumulative;
      });

      if (timeRange === '1W') return realPoints.slice(-7);
      if (timeRange === '1M') return realPoints.slice(-30);
    }
    
    switch(timeRange) {
      case '1D': // Last 24 hours (simulated 24 points)
        return [50, 51, 49, 52, 53, 52, 54, 55, 54, 56, 57, 55, 58, 59, 58, 60, 61, 60, 62, 63, 62, 64, 65, 64];
      case '1W': // Last 7 days
        return [50, 55, 52, 60, 58, 65, 68];
      case '1M': // Last 30 days
        return [50, 52, 48, 55, 58, 54, 60, 65, 62, 58, 63, 68, 72, 70, 75, 78, 74, 80, 85, 82, 88, 92, 90, 95, 98, 102, 100, 105, 110, 108];
      case '1Y': // Last 12 months
        return [50, 60, 55, 75, 85, 80, 100, 110, 105, 125, 140, 135];
      default:
        return Array(30).fill(50);
    }
  };

  const points = getChartData();
  const minPoint = Math.min(...points);
  const maxPoint = Math.max(...points);
  const range = maxPoint - minPoint || 1;
  
  // Normalize points to fit 0-100 scale (inverted for SVG where 0 is top)
  const normalizedPoints = points.map(p => 100 - ((p - minPoint) / range) * 80 - 10);
  const pathData = normalizedPoints.length > 1
    ? normalizedPoints.map((p, i) => `${(i / (normalizedPoints.length - 1)) * 100},${p}`).join(' L ')
    : `0,${normalizedPoints[0] || 50} L 100,${normalizedPoints[0] || 50}`;

  const getXLabels = () => {
    if (isLinked && dailyGrowth && dailyGrowth.length > 0) {
      const dates = dailyGrowth.slice(timeRange === '1W' ? -7 : -30).map(d => 
        new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
      );
      if (dates.length > 5) {
        return [dates[0], dates[Math.floor(dates.length/4)], dates[Math.floor(dates.length/2)], dates[Math.floor(3*dates.length/4)], dates[dates.length-1]];
      }
      return dates;
    }
    switch(timeRange) {
      case '1D': return ['00:00', '06:00', '12:00', '18:00', '23:59'];
      case '1W': return ['Jan 05', 'Jan 06', 'Jan 07', 'Jan 08', 'Jan 09', 'Jan 10', 'Jan 11'];
      case '1M': return ['Dec 12', 'Dec 18', 'Dec 24', 'Dec 30', 'Jan 05', 'Jan 11'];
      case '1Y': return ['Feb 25', 'Apr 25', 'Jun 25', 'Aug 25', 'Oct 25', 'Dec 25'];
      default: return [];
    }
  };

  const xLabels = getXLabels();

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[40px] p-4 sm:p-6 md:p-10 relative overflow-hidden">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 sm:gap-6 mb-6 sm:mb-10">
        <div>
          <h3 className="text-2xl font-black tracking-tight text-white uppercase">Growth Analysis</h3>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">Performance History ({timeRange})</p>
        </div>
        
        <div className="flex items-center gap-2 bg-white/5 p-1 rounded-2xl border border-white/10">
          {['1D', '1W', '1M', '1Y'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${timeRange === range ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[240px] w-full relative mt-4">
        {/* Y-Axis Grid Lines */}
            {[0, 25, 50, 75, 100].map((g) => (
              <div key={g} className="absolute left-0 right-0 border-b border-white/5" style={{ top: `${g}%` }}>
                <span className="absolute -left-2 -translate-x-full -translate-y-1/2 text-[8px] font-black text-white uppercase">
                  {isLinked ? (g === 50 ? '0%' : g < 50 ? `+${(50-g)*2}%` : `-${(g-50)*2}%`) : '0%'}
                </span>
              </div>
            ))}

        {/* Middle Baseline Indicator */}
        <div className="absolute left-0 right-0 border-b border-blue-500/20 top-1/2 z-0"></div>

        <svg className="w-full h-full overflow-visible relative z-10" preserveAspectRatio="none" viewBox="0 0 100 100">
          <defs>
            <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
          </defs>
          
          {/* Area Fill */}
          <motion.path
            key={`area-${timeRange}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            fill="url(#chartGradient)"
            d={`M 0,100 L ${pathData} L 100,100 Z`}
          />

          {/* Line */}
          <motion.path
            key={`line-${timeRange}`}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, ease: "easeInOut" }}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            d={`M ${pathData}`}
          />

          {/* Data Points */}
          {normalizedPoints.map((p, i) => (
            <motion.circle
              key={`${timeRange}-${i}`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 + i * (1/normalizedPoints.length) }}
              cx={(i / (normalizedPoints.length - 1)) * 100}
              cy={p}
              r="0.8"
              fill="#3b82f6"
              className="cursor-pointer hover:r-1.5 transition-all"
            />
          ))}
        </svg>

        {/* X-Axis Labels */}
            <div className="absolute -bottom-8 left-0 right-0 flex justify-between px-1">
              {xLabels.map((label, idx) => (
                <span key={idx} className="text-[8px] font-black text-white uppercase">{label}</span>
              ))}
            </div>
      </div>
    </div>
  );
};

const TradingViewAnalysisWidget = ({ activeSignal, masterStats }) => {
  const direction = activeSignal?.direction;
  const color = direction === 'BUY' ? 'text-green-500' : direction === 'SELL' ? 'text-red-400' : 'text-gray-400';
  const bgColor = direction === 'BUY' ? 'bg-green-500/10' : direction === 'SELL' ? 'bg-red-500/10' : 'bg-white/5';
  const borderColor = direction === 'BUY' ? 'border-green-500/20' : direction === 'SELL' ? 'border-red-500/20' : 'border-white/10';
  const dotColor = direction === 'BUY' ? 'bg-green-500' : direction === 'SELL' ? 'bg-red-500' : 'bg-gray-500';
  const label = direction || 'NO TRADE';

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex-1 flex flex-col items-center justify-center relative py-4">
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className={`w-36 h-36 rounded-full border border-dashed opacity-20 ${color.replace('text-', 'border-')}`}
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className={`w-28 h-28 rounded-full border border-dotted opacity-10 ${color.replace('text-', 'border-')}`}
          />
        </div>

        <div className="relative z-10 text-center">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${bgColor} border ${borderColor} mb-4`}>
            <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${dotColor}`} />
            <span className={`text-[9px] font-black uppercase tracking-[0.2em] ${color}`}>Master Position</span>
          </div>
          <h4 className={`text-4xl font-black italic uppercase tracking-tighter mb-2 ${color}`}>
            {label}
          </h4>
          <div className="flex items-center justify-center gap-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest opacity-60">Win Rate</span>
            <span className="text-[10px] font-black text-white tabular-nums">{masterStats ? `${masterStats.winRate.toFixed(1)}%` : '—'}</span>
          </div>
        </div>
      </div>

      {activeSignal ? (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center backdrop-blur-sm">
            <p className="text-[7px] font-black text-gray-500 uppercase mb-1.5 tracking-tighter">Entry</p>
            <p className="text-[10px] font-black text-white tabular-nums">{activeSignal.entry_price ? activeSignal.entry_price.toFixed(2) : '—'}</p>
          </div>
          <div className="bg-green-500/5 border border-green-500/10 rounded-2xl p-3 text-center backdrop-blur-sm">
            <p className="text-[7px] font-black text-gray-500 uppercase mb-1.5 tracking-tighter">Take Profit</p>
            <p className="text-[10px] font-black text-green-400 tabular-nums">{(activeSignal.tp || 0).toFixed(2)}</p>
          </div>
          <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-3 text-center backdrop-blur-sm">
            <p className="text-[7px] font-black text-gray-500 uppercase mb-1.5 tracking-tighter">Stop Loss</p>
            <p className="text-[10px] font-black text-red-400 tabular-nums">{(activeSignal.sl || 0).toFixed(2)}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center backdrop-blur-sm">
            <p className="text-[7px] font-black text-gray-500 uppercase mb-1.5 tracking-tighter">Trades</p>
            <p className="text-[10px] font-black text-white tabular-nums">{masterStats?.tradeCount || 0}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center backdrop-blur-sm">
            <p className="text-[7px] font-black text-gray-500 uppercase mb-1.5 tracking-tighter">Drawdown</p>
            <p className="text-[10px] font-black text-red-400 tabular-nums">{masterStats ? `${masterStats.maxDrawdown.toFixed(2)}%` : '—'}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center backdrop-blur-sm">
            <p className="text-[7px] font-black text-gray-500 uppercase mb-1.5 tracking-tighter">Status</p>
            <p className="text-[10px] font-black text-blue-400">STANDBY</p>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Page: Results ---
const FLEXBOT_SERVER = 'https://flexbot-qpf2.onrender.com';

const RiskGuardWidget = ({ compact = false }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${FLEXBOT_SERVER}/api/risk-status?symbol=XAUUSD`);
        const j = await res.json();
        if (j.ok) setData(j);
      } catch (e) {
        console.error('[RiskGuard]', e);
      }
    };
    fetchStatus();
    const i = setInterval(fetchStatus, 30000);
    return () => clearInterval(i);
  }, []);

  const ddPct = data?.daily_dd_pct || 0;
  const maxPct = data?.daily_dd_max_pct || 5;
  const fillPct = Math.min(100, (ddPct / maxPct) * 100);
  const status = data?.status || 'no_data';

  const label = { safe: 'SAFE', warning: 'WARNING', halted: 'HALTED', no_data: 'WAITING' }[status];
  const dotColor = status === 'halted' ? 'bg-red-500' : status === 'warning' ? 'bg-yellow-500' : status === 'safe' ? 'bg-green-500' : 'bg-gray-500';
  const barColor = status === 'halted' ? 'bg-red-500' : status === 'warning' ? 'bg-yellow-500' : 'bg-green-500';
  const badgeColors = status === 'halted' ? 'bg-red-500/10 text-red-500 border-red-500/20'
    : status === 'warning' ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
    : status === 'safe' ? 'bg-green-500/10 text-green-500 border-green-500/20'
    : 'bg-gray-500/10 text-gray-500 border-gray-500/20';

  return (
    <div className={`bg-white/[0.03] border border-white/[0.05] rounded-2xl sm:rounded-3xl ${compact ? 'p-4' : 'p-4 sm:p-6'} relative overflow-hidden`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center shrink-0">
            <Shield className="text-blue-500 w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-black text-white uppercase tracking-tighter leading-tight truncate">Risk Guard</h3>
            <p className="text-[9px] sm:text-[10px] font-medium text-gray-500 truncate">Daily loss limit · {maxPct.toFixed(2)}%</p>
          </div>
        </div>
        <div className={`px-2.5 py-1 rounded-full border ${badgeColors} flex items-center gap-1.5 shrink-0`}>
          <div className={`w-1.5 h-1.5 rounded-full ${dotColor} ${status === 'safe' ? 'animate-pulse' : ''}`}></div>
          <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest">{label}</span>
        </div>
      </div>

      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Daily Loss</span>
        <span className="text-sm sm:text-base font-black tabular-nums">
          <span className={ddPct > 0 ? 'text-red-400' : 'text-gray-400'}>{ddPct > 0 ? '−' : ''}{ddPct.toFixed(2)}%</span>
          <span className="text-gray-600 text-xs font-bold"> / {maxPct.toFixed(2)}%</span>
        </span>
      </div>

      <div className="relative w-full h-2 bg-white/5 rounded-full overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${barColor} rounded-full transition-all duration-700`} style={{ width: `${fillPct}%` }}></div>
      </div>

      {!data?.has_day_data && (
        <p className="text-[9px] font-medium text-gray-600 mt-3 italic">Waiting for live equity data from master EA…</p>
      )}
    </div>
  );
};

const ResultsPage = () => {
  const [trades, setTrades] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [risk, setRisk] = useState(null);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const res = await fetch(`${FLEXBOT_SERVER}/api/trades?limit=2000`);
        const data = await res.json();
        if (data.ok) {
          setTrades(data.trades || []);
          setAccount(data.account || null);
        }
      } catch (e) {
        console.error('[Results] Failed to fetch trades:', e);
      }
      setLoading(false);
    };
    const fetchRisk = async () => {
      try {
        const res = await fetch(`${FLEXBOT_SERVER}/api/risk-status?symbol=XAUUSD`);
        const data = await res.json();
        if (data.ok) setRisk(data);
      } catch (e) {
        console.error('[Results] Failed to fetch risk:', e);
      }
    };
    fetchTrades();
    fetchRisk();
    const interval = setInterval(() => { fetchTrades(); fetchRisk(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const todayKey = new Date().toISOString().split('T')[0];

  const parseResult = (r) => {
    if (!r) return 0;
    const cleaned = String(r).replace(/[^0-9.\-+]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Get week start (Monday) for a given date
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Current selected week range
  const now = new Date();
  const currentWeekStart = getWeekStart(now);
  const selectedWeekStart = new Date(currentWeekStart);
  selectedWeekStart.setDate(selectedWeekStart.getDate() + weekOffset * 7);
  const selectedWeekEnd = new Date(selectedWeekStart);
  selectedWeekEnd.setDate(selectedWeekEnd.getDate() + 6);

  const weekLabel = `${selectedWeekStart.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} — ${selectedWeekEnd.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  // Only show trades from the currently active master account (backend may still hold history from previous accounts).
  // Legacy IDs use the `m-{login}-{tradeId}` format — filter those by login. Newer IDs (e.g. `admin-...`) lack the
  // login segment and are accepted as-is. Also drop simulation/admin test trades, and dedupe by trade id —
  // the backend currently pushes the same trade multiple times with slightly different entry prices.
  const accountLogin = account?.login ? String(account.login) : null;
  const filtered = trades.filter(t => {
    const id = String(t.id || '');
    const outcome = String(t.outcome || '');
    if (id.startsWith('m-') && accountLogin && !id.startsWith(`m-${accountLogin}-`)) return false;
    if (id.toLowerCase().includes('test')) return false;
    if (outcome === 'SIM_CLOSE' || outcome === 'ADMIN_CLOSE') return false;
    if (outcome.toLowerCase().includes('test')) return false;
    return true;
  });
  const seenIds = new Set();
  const myTrades = filtered.filter(t => {
    const id = String(t.id || '');
    if (!id) return true;
    if (seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });

  // Filter trades for selected week (exclude weekends and trades without result)
  const weekTrades = myTrades.filter(t => {
    if (!t.result || t.outcome === 'closed') return false;
    const ts = t.closed_at || t.opened_at;
    if (!ts) return false;
    const d = new Date(ts);
    const day = d.getDay();
    if (day === 0 || day === 6) return false;
    return d >= selectedWeekStart && d <= new Date(selectedWeekEnd.getTime() + 86400000);
  });

  // Group filtered trades by day
  const tradesByDay = {};
  weekTrades.forEach(t => {
    const ts = t.closed_at || t.opened_at;
    if (!ts) return;
    const date = new Date(ts).toISOString().split('T')[0];
    if (!tradesByDay[date]) tradesByDay[date] = { trades: [], totalProfit: 0 };
    const profit = parseResult(t.result);
    tradesByDay[date].trades.push({ ...t, profitNum: profit });
    tradesByDay[date].totalProfit += profit;
  });

  const sortedDays = Object.keys(tradesByDay).sort((a, b) => new Date(b) - new Date(a));

  // Stats for selected week
  const weekProfit = weekTrades.reduce((acc, t) => acc + parseResult(t.result), 0);
  const weekWins = weekTrades.filter(t => t.outcome === 'TP' || parseResult(t.result) > 0).length;
  const weekWinRate = weekTrades.length > 0 ? ((weekWins / weekTrades.length) * 100).toFixed(1) : '0';

  // Check if there are trades in older weeks (for prev button)
  const oldestTrade = myTrades.length > 0 ? Math.min(...myTrades.map(t => t.closed_at || t.opened_at || Infinity)) : null;
  const oldestWeekStart = oldestTrade ? getWeekStart(new Date(oldestTrade)) : null;
  const canGoPrev = oldestWeekStart ? selectedWeekStart > oldestWeekStart : false;

  return (
    <div className="relative min-h-screen pt-12 sm:pt-24 pb-12 sm:pb-20">
      <SEO
        title="Live Results — FlexBot AI Audited Gold Trading Performance"
        description="Verified live MT5 trading results for FlexBot AI. Real master account performance on XAUUSD, updated every 30 seconds. Full transparency, no demo numbers."
        path="/results"
      />
      <div className="absolute inset-0 bg-blue-600/[0.02] pointer-events-none"></div>
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/5 border border-green-500/10 text-green-500 text-[10px] font-black uppercase tracking-widest mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div> Live MT5 Verification
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-black tracking-tighter mb-4 text-white uppercase">Live <span className="text-gray-500">Results</span></h1>
          <p className="text-gray-400 max-w-2xl mx-auto font-medium leading-relaxed">
            Real-time performance tracked directly from our Master EA account. Complete transparency.
          </p>
        </div>

        {/* Trading Journal */}
        <div className="max-w-6xl mx-auto bg-white/[0.02] border border-white/[0.05] rounded-2xl sm:rounded-[48px] p-4 sm:p-6 md:p-12 overflow-hidden relative shadow-2xl">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-500/[0.03] blur-[150px] rounded-full -z-10 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-green-500/[0.02] blur-[150px] rounded-full -z-10"></div>

          {/* Header */}
          <div className="mb-6 sm:mb-8 border-b border-white/5 pb-4 sm:pb-6 space-y-3 sm:space-y-4">
            {/* Title row */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                <LayoutDashboard className="text-blue-500 w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg md:text-xl font-black text-white uppercase tracking-tighter leading-tight truncate">Trading Journal</h3>
                <p className="text-gray-500 font-medium text-[9px] sm:text-[10px] truncate">Master EA {account ? `— ${account.server}` : ''}</p>
              </div>
            </div>

            {/* Week selector + stats row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {/* Week selector */}
              <div className="flex items-center gap-1 bg-white/5 border border-white/10 p-1 rounded-full">
                <button
                  onClick={() => canGoPrev && setWeekOffset(prev => prev - 1)}
                  disabled={!canGoPrev}
                  className={`p-1.5 rounded-full transition-all shrink-0 ${!canGoPrev ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/10 hover:text-white text-gray-400'}`}
                >
                  <ChevronLeft size={14} />
                </button>
                <div className="flex-1 px-2 text-[9px] sm:text-[10px] font-black text-white tracking-wider uppercase text-center whitespace-nowrap">
                  {weekLabel}
                </div>
                <button
                  onClick={() => weekOffset < 0 && setWeekOffset(prev => prev + 1)}
                  disabled={weekOffset >= 0}
                  className={`p-1.5 rounded-full transition-all shrink-0 ${weekOffset >= 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/10 hover:text-white text-gray-400'}`}
                >
                  <ChevronRight size={14} />
                </button>
              </div>

              {/* Weekly stats */}
              <div className="grid grid-cols-3 bg-white/5 rounded-full border border-white/10 overflow-hidden flex-1">
                <div className="text-center py-2 px-2">
                  <p className="text-[7px] sm:text-[8px] font-black text-gray-500 uppercase tracking-widest leading-none mb-0.5">Win</p>
                  <p className="text-xs sm:text-sm font-black text-white tabular-nums leading-none">{weekWinRate}%</p>
                </div>
                <div className="text-center py-2 px-2 border-x border-white/10">
                  <p className="text-[7px] sm:text-[8px] font-black text-gray-500 uppercase tracking-widest leading-none mb-0.5">Trades</p>
                  <p className="text-xs sm:text-sm font-black text-white tabular-nums leading-none">{weekTrades.length}</p>
                </div>
                <div className="text-center py-2 px-2 min-w-0">
                  <p className="text-[7px] sm:text-[8px] font-black text-gray-500 uppercase tracking-widest leading-none mb-0.5">P/L</p>
                  <p className={`text-xs sm:text-sm font-black tabular-nums truncate leading-none ${weekProfit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {weekProfit >= 0 ? '+' : '-'}${Math.abs(weekProfit).toFixed(0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-20 flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
              <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Connecting to Master MT5...</p>
            </div>
          ) : (() => {
            // Always show today's card on current week (with risk bar even if no trades yet)
            const showSyntheticToday = weekOffset === 0 && !sortedDays.includes(todayKey);
            const displayDays = showSyntheticToday ? [todayKey, ...sortedDays] : sortedDays;
            if (displayDays.length === 0) {
              return (
                <div className="py-20 text-center">
                  <Activity size={32} className="text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">No trades this week</p>
                </div>
              );
            }
            return (
            <div className="space-y-4">
              {/* Headers */}
              <div className="hidden md:grid grid-cols-5 gap-4 px-6 pb-2 text-[10px] font-black text-gray-500 uppercase tracking-widest">
                <div>Date</div>
                <div>Trades</div>
                <div>Net P/L ($)</div>
                <div>Type</div>
                <div className="text-right">Symbol</div>
              </div>

              {/* Group by day */}
              {displayDays.map((dateKey, di) => {
                const dayData = tradesByDay[dateKey] || { trades: [], totalProfit: 0 };
                const dayProfit = dayData.totalProfit;
                const dayWin = dayProfit >= 0;
                const displayDate = new Date(dateKey).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                const tpCount = dayData.trades.filter(t => t.outcome === 'TP').length;
                const slCount = dayData.trades.filter(t => t.outcome === 'SL').length;

                return (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: di * 0.05 }}
                    key={dateKey}
                    className="bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.05] hover:border-white/10 rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-300"
                  >
                    {/* Day header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6 md:px-8 md:py-6">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg shrink-0 ${dayWin ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                          {dayData.trades.length}
                        </div>
                        <div>
                          <p className="text-base sm:text-lg font-black text-white">{displayDate}{dateKey === todayKey && <span className="ml-2 text-[9px] font-black text-blue-400 bg-blue-500/10 border border-blue-500/20 px-1.5 py-0.5 rounded uppercase tracking-widest align-middle">Today</span>}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {tpCount > 0 && <span className="text-[9px] font-black text-green-500 bg-green-500/10 px-2 py-0.5 rounded">{tpCount} TP</span>}
                            {slCount > 0 && <span className="text-[9px] font-black text-red-400 bg-red-500/10 px-2 py-0.5 rounded">{slCount} SL</span>}
                            <span className="text-[9px] font-bold text-gray-600 uppercase">{dayData.trades[0]?.symbol || 'XAUUSD'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 sm:gap-6">
                        <p className={`text-xl sm:text-2xl md:text-3xl font-black tracking-tight tabular-nums ${dayData.trades.length === 0 ? 'text-gray-500' : dayWin ? 'text-green-500' : 'text-red-400'}`}>
                          {dayData.trades.length === 0 ? '—' : dayProfit !== 0 ? `${dayWin ? '+' : '-'}$${Math.abs(dayProfit).toFixed(2)}` : (tpCount > slCount ? 'WIN' : 'LOSS')}
                        </p>
                      </div>
                    </div>

                    {/* Risk Guard — only on today's card */}
                    {dateKey === todayKey && risk && (() => {
                      const ddPct = risk.daily_dd_pct || 0;
                      const maxPct = risk.daily_dd_max_pct || 5;
                      const fillPct = Math.min(100, (ddPct / maxPct) * 100);
                      const status = risk.status || 'no_data';
                      const label = { safe: 'SAFE', warning: 'WARNING', halted: 'HALTED', no_data: 'WAITING' }[status];
                      const dotColor = status === 'halted' ? 'bg-red-500' : status === 'warning' ? 'bg-yellow-500' : status === 'safe' ? 'bg-green-500' : 'bg-gray-500';
                      const barColor = status === 'halted' ? 'bg-red-500' : status === 'warning' ? 'bg-yellow-500' : 'bg-green-500';
                      const txtColor = status === 'halted' ? 'text-red-500' : status === 'warning' ? 'text-yellow-500' : status === 'safe' ? 'text-green-500' : 'text-gray-500';
                      return (
                        <div className="border-t border-white/5 px-4 sm:px-6 md:px-8 py-3 sm:py-4 bg-white/[0.015]">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Shield size={12} className="text-blue-400 shrink-0" />
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">Daily Loss Limit</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs font-black tabular-nums">
                                <span className={ddPct > 0 ? 'text-red-400' : 'text-gray-400'}>{ddPct > 0 ? '−' : ''}{ddPct.toFixed(2)}%</span>
                                <span className="text-gray-600"> / {maxPct.toFixed(2)}%</span>
                              </span>
                              <span className={`flex items-center gap-1 ${txtColor}`}>
                                <div className={`w-1.5 h-1.5 rounded-full ${dotColor} ${status === 'safe' ? 'animate-pulse' : ''}`}></div>
                                <span className="text-[9px] font-black uppercase tracking-widest">{label}</span>
                              </span>
                            </div>
                          </div>
                          <div className="relative w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className={`absolute inset-y-0 left-0 ${barColor} rounded-full transition-all duration-700`} style={{ width: `${fillPct}%` }}></div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Individual trades table */}
                    {dayData.trades.length > 0 && (
                    <div className="border-t border-white/5">
                      {dayData.trades.map((t, ti) => {
                        const isTP = t.outcome && t.outcome.toUpperCase().includes('TP');
                        const isSL = t.outcome && t.outcome.toUpperCase().includes('SL');
                        return (
                          <div key={ti} className={`flex items-center justify-between gap-2 px-3 sm:px-6 md:px-8 py-3 sm:py-4 ${ti !== dayData.trades.length - 1 ? 'border-b border-white/[0.03]' : ''} hover:bg-white/[0.02] transition-colors`}>
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                              <span className={`w-12 sm:w-14 text-center px-2 py-1 rounded-lg text-[10px] sm:text-xs font-black shrink-0 ${t.direction === 'BUY' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                                {t.direction}
                              </span>
                              <span className="text-sm sm:text-base font-mono font-bold text-white tabular-nums shrink-0">
                                {t.closed_at ? new Date(t.closed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                              </span>
                              {t.entry_price && <span className="text-sm sm:text-base font-bold text-gray-300 tabular-nums truncate">@ {t.entry_price}</span>}
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                              <span className={`text-sm sm:text-base font-black tabular-nums ${t.profitNum >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {t.result ? t.result : (t.profitNum !== 0 ? `${t.profitNum >= 0 ? '+' : ''}$${Math.abs(t.profitNum).toFixed(2)}` : '—')}
                              </span>
                              <span className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10px] sm:text-xs font-black uppercase ${isTP ? 'bg-green-500/15 text-green-400 border border-green-500/30' : isSL ? 'bg-red-500/15 text-red-400 border border-red-500/30' : 'bg-white/5 text-gray-400 border border-white/10'}`}>
                                {isTP ? 'TP' : isSL ? 'SL' : '—'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

// --- Page: Myfxbook Audit ---
const MYFXBOOK_PHASES = [
  {
    id: 'phase1',
    label: 'Phase 1',
    fullLabel: 'Phase 1 — Challenge',
    status: 'active',
    target: '+8% profit · 30 day limit',
    description: "First evaluation phase of the FTMO Challenge. FlexBot's live execution is being tracked and independently audited on Myfxbook.",
    url: 'https://www.myfxbook.com/members/FlexbotAI/flexbot-ftmo-challenge-phase-1/12038475',
  },
  {
    id: 'phase2',
    label: 'Phase 2',
    fullLabel: 'Phase 2 — Verification',
    status: 'locked',
    target: '+5% profit · 60 day limit',
    description: 'Unlocks once Phase 1 is passed. The verification account audit will appear here automatically.',
    url: null,
  },
  {
    id: 'funded',
    label: 'Funded',
    fullLabel: 'Funded Account',
    status: 'locked',
    target: 'Live capital · real payouts',
    description: 'Unlocks once Phase 2 is passed. The real-money funded account where FlexBot trades for verified payouts.',
    url: null,
  },
];

const MyfxbookPage = () => {
  const [activePhase, setActivePhase] = useState('phase1');
  const phase = MYFXBOOK_PHASES.find(p => p.id === activePhase) || MYFXBOOK_PHASES[0];
  const isLocked = phase.status === 'locked';

  return (
    <div className="relative min-h-screen pt-12 sm:pt-24 pb-12 sm:pb-20">
      <SEO
        title="Myfxbook Verification — FlexBot AI Phase Tracking"
        description="Track FlexBot AI's prop-firm challenge phases verified on Myfxbook. Independent third-party verification of every stage of the journey."
        path="/myfxbook"
      />
      <div className="absolute inset-0 bg-blue-600/[0.02] pointer-events-none"></div>
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Hero */}
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/5 border border-blue-500/10 text-blue-400 text-[10px] font-black uppercase tracking-widest mb-6">
            <Award className="w-3 h-3" /> FTMO Challenge — Verified Audit
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-black tracking-tighter mb-4 text-white uppercase">
            Myfxbook <span className="text-gray-500">Audit</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto font-medium leading-relaxed">
            Independently verified prop-firm progression. Every trade is auditable on Myfxbook — no edits, no hiding.
          </p>
        </div>

        {/* Phase Tabs */}
        <div className="max-w-4xl mx-auto mb-6 flex bg-white/5 border border-white/10 p-1 rounded-full">
          {MYFXBOOK_PHASES.map(p => {
            const isActive = p.id === activePhase;
            const locked = p.status === 'locked';
            return (
              <button
                key={p.id}
                onClick={() => setActivePhase(p.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 sm:px-3 rounded-full text-[9px] sm:text-[11px] font-black uppercase tracking-[0.18em] transition-all ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {locked && <Lock size={9} className="opacity-60" />}
                <span className="truncate">{p.label}</span>
              </button>
            );
          })}
        </div>

        {/* Phase Card */}
        <div className="max-w-4xl mx-auto bg-white/[0.02] border border-white/[0.05] rounded-2xl sm:rounded-[48px] p-6 sm:p-10 md:p-14 overflow-hidden relative shadow-2xl">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-500/[0.03] blur-[150px] rounded-full -z-10 animate-pulse pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-green-500/[0.02] blur-[150px] rounded-full -z-10 pointer-events-none"></div>

          {isLocked ? (
            <div className="py-8 sm:py-14 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6">
                <Lock size={22} className="text-gray-500" />
              </div>
              <h2 className="text-xl sm:text-3xl font-black uppercase tracking-tighter text-white mb-3">{phase.fullLabel}</h2>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/5 border border-yellow-500/10 text-yellow-400 text-[9px] font-black uppercase tracking-widest mb-6">
                <Lock size={9} /> Locked
              </div>
              <p className="text-gray-400 max-w-md font-medium text-sm leading-relaxed mb-5">
                {phase.description}
              </p>
              <p className="text-gray-600 text-[10px] font-black uppercase tracking-widest">Target: {phase.target}</p>
            </div>
          ) : (
            <>
              {/* Phase header */}
              <div className="border-b border-white/5 pb-5 sm:pb-6 mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                    <Award className="text-blue-500 w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-2xl font-black uppercase tracking-tighter text-white truncate">{phase.fullLabel}</h2>
                    <p className="text-gray-500 font-medium text-[10px] sm:text-xs truncate">FTMO · {phase.target}</p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/5 border border-green-500/10 text-green-500 text-[9px] font-black uppercase tracking-widest self-start sm:self-auto shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div> Live
                </div>
              </div>

              <p className="text-gray-400 text-sm leading-relaxed mb-8">{phase.description}</p>

              <a
                href={phase.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center gap-3 w-full bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-[0.2em] text-[11px] sm:text-xs py-4 sm:py-5 rounded-2xl transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40"
              >
                View Full Audit on Myfxbook
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
              </a>

              <p className="text-center text-gray-600 text-[9px] font-black uppercase tracking-widest mt-4">
                Live data hosted on Myfxbook · independently verified
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Page: Referral Leaderboard ---
const LeaderboardPage = () => {
  const [board, setBoard] = useState([]);
  const [weights, setWeights] = useState({ paid: 10, group: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${FLEXBOT_SERVER}/api/leaderboard`, { cache: 'no-store' });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error || 'unknown');
        setBoard(data.leaderboard || []);
        if (data.weights) setWeights(data.weights);
        setError(false);
      } catch (e) {
        console.error('[Leaderboard] failed:', e);
        setError(true);
      }
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const now = new Date();
  const monthName = now.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const daysLeft = Math.max(0, Math.ceil((nextMonth - now) / (24 * 3600 * 1000)));

  const medals = ['🥇', '🥈', '🥉'];
  const nameClass = (rank) => {
    if (rank === 1) return 'text-yellow-400 font-bold';
    if (rank === 2) return 'text-gray-300 font-semibold';
    if (rank === 3) return 'text-amber-600 font-semibold';
    return '';
  };

  return (
    <div className="relative min-h-screen py-12 sm:py-16 px-4 sm:px-6">
      <SEO
        title="Referral Leaderboard — FlexBot AI"
        description="Monthly FlexBot AI referral leaderboard. Top inviters compete for cash prizes and bonuses. Live ranking updated in real time."
        path="/leaderboard"
      />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center pt-4 pb-8 sm:pb-10 px-4">
          <div className="text-yellow-400 text-[13px] font-bold tracking-[0.2em] mb-2">FLEXBOT</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">🏆 Referral Leaderboard</h1>
          <div className="text-gray-500 text-sm">
            {loading ? 'Loading…' : `${monthName} · Top inviters this month`}
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 gap-3 mb-7">
          <div className="bg-[#15151a] border border-white/10 rounded-xl p-4 sm:p-5 text-center">
            <div className="text-gray-500 text-[11px] uppercase tracking-[0.12em] mb-2">Inviters</div>
            <div className="text-2xl font-bold tabular-nums">{loading ? '—' : board.length}</div>
          </div>
          <div className="bg-[#15151a] border border-white/10 rounded-xl p-4 sm:p-5 text-center">
            <div className="text-gray-500 text-[11px] uppercase tracking-[0.12em] mb-2">Days remaining</div>
            <div className="text-2xl font-bold tabular-nums">{daysLeft}</div>
          </div>
        </div>

        {/* Table card */}
        <div className="bg-[#15151a] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-[15px] sm:text-base">
            <thead>
              <tr>
                <th className="bg-[#0a0a0c] text-gray-500 text-[11px] uppercase tracking-[0.12em] font-medium text-left py-3 px-2 sm:px-3.5 border-b border-white/10">Rank</th>
                <th className="bg-[#0a0a0c] text-gray-500 text-[11px] uppercase tracking-[0.12em] font-medium text-left py-3 px-2 sm:px-3.5 border-b border-white/10">Name</th>
                <th className="bg-[#0a0a0c] text-gray-500 text-[11px] uppercase tracking-[0.12em] font-medium text-right py-3 px-2 sm:px-3.5 border-b border-white/10 tabular-nums">Points</th>
                <th className="bg-[#0a0a0c] text-gray-500 text-[11px] uppercase tracking-[0.12em] font-medium text-right py-3 px-2 sm:px-3.5 border-b border-white/10 tabular-nums">Paid</th>
                <th className="bg-[#0a0a0c] text-gray-500 text-[11px] uppercase tracking-[0.12em] font-medium text-right py-3 px-2 sm:px-3.5 border-b border-white/10 tabular-nums">Group</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-8 text-gray-500 text-sm">Loading leaderboard…</td></tr>
              ) : error ? (
                <tr><td colSpan="5" className="text-center py-10 text-gray-500 text-sm">Couldn't load leaderboard right now. Try again in a moment.</td></tr>
              ) : board.length === 0 ? (
                <tr><td colSpan="5" className="text-center py-10 text-gray-500 text-sm">No invites yet this month — be the first 🚀</td></tr>
              ) : board.map((entry, i, arr) => {
                const rank = entry.rank || (i + 1);
                const isTop3 = rank <= 3;
                const isLast = i === arr.length - 1;
                const points = entry.points != null ? entry.points : (entry.invites || 0);
                const paid = entry.paid_invites != null ? entry.paid_invites : 0;
                const grp = entry.group_invites != null ? entry.group_invites : 0;
                const cellBase = `py-3 sm:py-3.5 px-2 sm:px-3.5 ${isLast ? '' : 'border-b border-white/10'}`;
                return (
                  <tr key={`${entry.name || ''}-${i}`}>
                    <td className={`${cellBase} text-xl sm:text-[22px] font-bold w-14`}>
                      {isTop3 ? medals[rank - 1] : <span className="text-gray-500 text-base font-semibold">{rank}</span>}
                    </td>
                    <td className={`${cellBase} ${nameClass(rank)}`}>{entry.name || 'Anonymous'}</td>
                    <td className={`${cellBase} text-right tabular-nums text-yellow-400 font-bold text-base sm:text-lg w-16 sm:w-20`}>{points}</td>
                    <td className={`${cellBase} text-right tabular-nums text-white font-semibold w-12 sm:w-16`}>{paid}</td>
                    <td className={`${cellBase} text-right tabular-nums text-gray-500 font-semibold w-12 sm:w-16`}>{grp}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Scoring legend */}
        <div className="mt-3.5 py-3 px-4 bg-[#15151a] border border-white/10 rounded-lg text-gray-500 text-xs text-center leading-relaxed">
          Scoring: <b className="text-white">1 paid customer = {weights.paid} pts</b> · <b className="text-white">1 verified group join = {weights.group} pt{weights.group === 1 ? '' : 's'}</b>
        </div>

        {/* Prize */}
        <div className="mt-6 bg-gradient-to-br from-yellow-400/[0.12] to-yellow-400/[0.04] border border-yellow-400/30 rounded-xl p-5 text-center">
          <div className="text-yellow-400 text-[11px] uppercase tracking-[0.16em] font-bold mb-1.5">🎁 Monthly Prize</div>
          <div className="text-gray-200 text-sm leading-relaxed">
            #1 at the end of the month wins <b>+30 days free</b> on their FlexBot license.<br />
            Auto-applied. Announced in the community group on the 1st.
          </div>
        </div>

        {/* Footer hint */}
        <div className="text-center mt-8 text-gray-500 text-xs">
          Get your invite link in the community group: <code className="text-yellow-400">/myref</code>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ tradingLogs, onBuyClick }) => {
  const navigate = useNavigate();
  const [, setCurrentGoldPrice] = useState(4432.69);
  const [, setPriceHistory] = useState([]);
  const [isInitialAnalysis, setIsInitialAnalysis] = useState(true);
  const [lastSignalTime, setLastSignalTime] = useState(0);
  const [analysis, setAnalysis] = useState({ target: 0, stopLoss: 0, confidence: 0, reason: "Initializing Neural Core...", sentiment: "Analyzing" });
  const [activeSignal, setActiveSignal] = useState(null);
  const [masterStats, setMasterStats] = useState(null);

  // Fetch real active signal + master account stats from server
  useEffect(() => {
    const fetchServerData = async () => {
      try {
        const [sigRes, tradesRes] = await Promise.all([
          fetch(`${FLEXBOT_SERVER}/api/active-signal?symbol=XAUUSD`),
          fetch(`${FLEXBOT_SERVER}/api/trades?limit=2000`),
        ]);
        const sigData = await sigRes.json();
        const tradesData = await tradesRes.json();
        if (sigData.ok) setActiveSignal(sigData.signal);
        if (tradesData.ok) {
          const START_BALANCE = 100000;
          const LIVE_START_MS = new Date('2026-05-08T00:00:00Z').getTime();
          const parseR = (r) => parseFloat(String(r).replace(/[^0-9.\-+]/g, '')) || 0;
          const activeLogin = tradesData.account?.login ? String(tradesData.account.login) : null;
          const seenStatIds = new Set();
          const trades = (tradesData.trades || []).filter(t => {
            const id = String(t.id || '');
            const outcome = String(t.outcome || '');
            if (!t.result) return false;
            if (outcome === 'closed' || outcome === 'SIM_CLOSE' || outcome === 'ADMIN_CLOSE') return false;
            if ((t.closed_at || 0) < LIVE_START_MS) return false;
            if (id.startsWith('m-') && activeLogin && !id.startsWith(`m-${activeLogin}-`)) return false;
            if (id.toLowerCase().includes('test')) return false;
            if (outcome.toLowerCase().includes('test')) return false;
            if (id && seenStatIds.has(id)) return false;
            if (id) seenStatIds.add(id);
            return true;
          });
          const wins = trades.filter(t => parseR(t.result) > 0).length;
          const winRate = trades.length > 0 ? (wins / trades.length) * 100 : 0;
          const balance = tradesData.account?.balance || START_BALANCE;
          const equity = tradesData.account?.equity || balance;
          const totalProfit = equity - START_BALANCE;
          // Max drawdown from initial deposit (FTMO-style "overall loss"):
          // (initial - current equity) / initial * 100, floored at 0 when in profit.
          const maxDDPct = Math.max(0, (START_BALANCE - equity) / START_BALANCE * 100);
          setMasterStats({
            equity,
            balance,
            totalProfit,
            winRate,
            maxDrawdown: maxDDPct,
            tradeCount: trades.length,
          });
        }
      } catch (e) {
        console.error('[Dashboard] Failed to fetch server data:', e);
      }
    };
    fetchServerData();
    const interval = setInterval(fetchServerData, 15000);
    return () => clearInterval(interval);
  }, []);

  // --- FlexBot Account State (uses our own backend, not MetaApi) ---
  const [isLinked, setIsLinked] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [formData, setFormData] = useState({
    accountId: '',
    startBalance: '100000',
  });
  const [accountInfo, setAccountInfo] = useState({
    id: '',
    server: '',
    balance: 0,
    equity: 0,
    profit: 0,
    trades: [],
    metrics: {
      winRate: 0,
      profitFactor: 0,
      totalTrades: 0,
      averageWin: 0,
      averageLoss: 0,
      maxDrawdown: 0,
    },
  });

  // Auto-link if login saved in localStorage
  useEffect(() => {
    const savedLogin = localStorage.getItem('flexbot_login');
    const savedBalance = localStorage.getItem('flexbot_start_balance');
    if (savedLogin && !isLinked) {
      setFormData({ accountId: savedLogin, startBalance: savedBalance || '100000' });
      const autoLink = async () => {
        setIsLinking(true);
        try {
          const res = await fetch(`${SERVER_URL}/api/trades?limit=2000`);
          const json = await res.json();
          if (!json.ok) return;
          const trades = filterTradesForLogin(json.trades, savedLogin);
          if (trades.length === 0) return;
          const startBal = Number(savedBalance) || 100000;
          const isMaster = json.account && String(json.account.login) === savedLogin;
          const stats = computeStatsFromTrades(trades, startBal, isMaster ? json.account : null);
          setAccountInfo({ id: savedLogin, server: 'FlexBot Tracked', isSimulated: false, ...stats });
          setIsLinked(true);
        } catch (err) {
          console.error('[AutoLink] Error:', err);
        } finally {
          setIsLinking(false);
        }
      };
      autoLink();
    }
  }, [isLinked]);

  const handleLinkAccount = async (e) => {
    if (e) e.preventDefault();
    setIsLinking(true);
    setLinkError('');

    const login = formData.accountId.trim();
    const startBal = Number(formData.startBalance) || 100000;
    if (!login) { setLinkError('Enter your MT5 login number.'); setIsLinking(false); return; }

    try {
      const res = await fetch(`${SERVER_URL}/api/trades?limit=2000`);
      const json = await res.json();
      if (!json.ok) throw new Error('Backend unreachable');
      const trades = filterTradesForLogin(json.trades, login);
      if (trades.length === 0) {
        setLinkError(`No trades found for account ${login}. Is your EA running and pushing trades?`);
        setIsLinking(false);
        return;
      }
      const isMaster = json.account && String(json.account.login) === login;
      const stats = computeStatsFromTrades(trades, startBal, isMaster ? json.account : null);
      if (rememberMe) {
        localStorage.setItem('flexbot_login', login);
        localStorage.setItem('flexbot_start_balance', String(startBal));
      } else {
        localStorage.removeItem('flexbot_login');
        localStorage.removeItem('flexbot_start_balance');
      }
      setAccountInfo({ id: login, server: 'FlexBot Tracked', isSimulated: false, ...stats });
      setIsLinked(true);
    } catch (err) {
      console.error('[Link] Error:', err);
      setLinkError('Something went wrong fetching your trades. Try again.');
    } finally {
      setIsLinking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('flexbot_login');
    localStorage.removeItem('flexbot_start_balance');
    setIsLinked(false);
    setFormData({ accountId: '', startBalance: '100000' });
    setAccountInfo({
      id: '', server: '', balance: 0, equity: 0, profit: 0, trades: [],
      metrics: { winRate: 0, profitFactor: 0, totalTrades: 0, averageWin: 0, averageLoss: 0, maxDrawdown: 0 },
    });
  };

  // Poll backend every 30s to refresh stats
  useEffect(() => {
    if (!isLinked || !accountInfo.id) return;
    const startBal = Number(localStorage.getItem('flexbot_start_balance')) || 100000;
    const refresh = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/trades?limit=2000`);
        const json = await res.json();
        if (!json.ok) return;
        const trades = filterTradesForLogin(json.trades, accountInfo.id);
        if (trades.length === 0) return;
        const isMaster = json.account && String(json.account.login) === accountInfo.id;
        const stats = computeStatsFromTrades(trades, startBal, isMaster ? json.account : null);
        setAccountInfo(prev => ({ ...prev, ...stats }));
      } catch (err) { /* noop */ }
    };
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [isLinked, accountInfo.id]);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialAnalysis(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=PAXGUSDT');
        if (!response.ok) return;
        const data = await response.json();
        const price = parseFloat(data.price);
        setCurrentGoldPrice(price);
        setPriceHistory(prev => {
          const newHistory = [...prev, price].slice(-200);
          const now = Date.now();
          
          // --- REAL TECHNICAL ANALYSIS ---
          const sma20 = calculateSMA(newHistory, 20);
          const sma50 = calculateSMA(newHistory, 50);
          const rsi = calculateRSI(newHistory, 14);
          
          let movingAvg = 'NEUTRAL';
          if (sma20 && sma50) {
            if (price > sma20 && sma20 > sma50) movingAvg = 'BUY';
            else if (price < sma20 && sma20 < sma50) movingAvg = 'SELL';
          }
          
          let oscillator = 'NEUTRAL';
          if (rsi > 70) oscillator = 'SELL'; // Overbought
          else if (rsi < 30) oscillator = 'BUY'; // Oversold
          
          const totalChange = price - (newHistory[0] || price);
          let sentiment, reason, confidence, target, stopLoss;
          
          if (movingAvg === 'BUY' && oscillator === 'BUY') { sentiment = "Strong Buy"; reason = "Neural & Technical Alignment (SMA+RSI)"; confidence = (96 + Math.random() * 2).toFixed(1); target = price + 15.00; stopLoss = price - 7.50; }
          else if (movingAvg === 'BUY' || totalChange > 2.0) { sentiment = "Bullish / Buy"; reason = "Trend Expansion detected"; confidence = (88 + Math.random() * 5).toFixed(1); target = price + 8.50; stopLoss = price - 4.20; }
          else if (movingAvg === 'SELL' && oscillator === 'SELL') { sentiment = "Strong Sell"; reason = "Institutional Liquidity Exit"; confidence = (94 + Math.random() * 3).toFixed(1); target = price - 18.20; stopLoss = price + 9.10; }
          else if (movingAvg === 'SELL' || totalChange < -2.0) { sentiment = "Bearish / Sell"; reason = "Market Structure Break"; confidence = (85 + Math.random() * 6).toFixed(1); target = price - 9.40; stopLoss = price + 4.80; }
          else { sentiment = "Neutral / Wait"; reason = "M15 Equilibrium Zone"; confidence = (82 + Math.random() * 4).toFixed(1); target = price + 1.50; stopLoss = price - 1.50; }
          
          if (!isInitialAnalysis) {
            setAnalysis(prev => {
              const shouldUpdate = now - lastSignalTime > 900000 || lastSignalTime === 0;
              if (shouldUpdate) {
                setLastSignalTime(now);
                return { target, stopLoss, confidence, reason, sentiment, movingAvg, oscillator, liquidity: 'STABLE' };
              }
              return prev;
            });
          } else {
            const thinkingMsgs = ["Scanning M15 Liquidity Pools...", "Calculating Neural Divergence...", "Analyzing Smart Money Pressure..."];
            setAnalysis(prev => ({ ...prev, reason: thinkingMsgs[Math.floor(Date.now() / 1500) % thinkingMsgs.length] }));
          }
          return newHistory;
        });
      } catch (e) {}
    };
    const interval = setInterval(fetchPrice, 4000);
    fetchPrice();
    return () => clearInterval(interval);
  }, [isInitialAnalysis, lastSignalTime, analysis.sentiment]);

  return (
    <div className="min-h-screen p-3 sm:p-4 md:p-6 lg:p-10 flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-10 relative">
      <Helmet>
        <title>Dashboard — FlexBot AI</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <aside className="w-full lg:w-72 flex flex-col gap-4 sm:gap-6 lg:sticky lg:top-32 lg:self-start">
        {/* Navigation Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[32px] p-4 sm:p-6 shadow-xl relative overflow-hidden group">
          <div className="space-y-4 sm:space-y-6 relative z-10">
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-2 hidden sm:block">Navigation</p>
            <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-x-visible">
              {[
                { label: 'Overview', icon: <PieChart size={18} /> }, 
                { label: 'Account', icon: <Wallet size={18} /> }, 
                { label: 'Settings', icon: <Settings size={18} /> }
              ].map((item, i) => (
                <button 
                  key={i} 
                  onClick={() => setActiveTab(item.label)}
                  className={`w-full flex items-center gap-4 px-4 py-3 rounded-2xl text-sm font-bold transition-all ${activeTab === item.label ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                >
                  {item.icon}{item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/5">
            <button onClick={() => navigate('/')} className="flex items-center gap-4 px-4 py-2 text-gray-500 hover:text-white font-bold text-xs uppercase tracking-widest transition-colors group">
              <Home size={16} className="group-hover:scale-110 transition-transform" /> Back to Home
            </button>
          </div>
        </div>

        {/* Dynamic Sidebar Card */}
        {activeTab === 'Account' && !isLinked ? (
          <div className="bg-black/40 border border-white/10 rounded-[32px] p-6 shadow-2xl backdrop-blur-md relative overflow-hidden border-t-blue-500/20">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center text-blue-500 border border-blue-500/20 shadow-inner"><Wallet size={20} /></div>
              <div>
                <h3 className="text-[10px] font-black text-white uppercase tracking-tight">View My Stats</h3>
                <p className="text-[8px] font-bold text-gray-500 uppercase tracking-widest mt-0.5 italic">Enter your MT5 login</p>
              </div>
            </div>

            <p className="text-[10px] font-medium text-gray-500 leading-relaxed mb-4">
              Your EA pushes trades to FlexBot's server automatically. Enter your MT5 login number to view your tracked stats.
            </p>

            <form onSubmit={handleLinkAccount} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[8px] font-black text-gray-500 uppercase tracking-[0.2em] ml-1">MT5 Login Number</label>
                <input
                  required
                  name="accountId"
                  type="text"
                  inputMode="numeric"
                  value={formData.accountId}
                  onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                  placeholder="e.g. 12033719"
                  className="w-full bg-white/[0.03] border border-white/10 px-4 py-2.5 rounded-xl text-white text-[10px] font-bold focus:outline-none focus:border-blue-500 transition-all placeholder:text-gray-700 shadow-inner"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[8px] font-black text-gray-500 uppercase tracking-[0.2em] ml-1">Starting Balance ($)</label>
                <input
                  required
                  name="startBalance"
                  type="number"
                  min="100"
                  step="100"
                  value={formData.startBalance}
                  onChange={(e) => setFormData({ ...formData, startBalance: e.target.value })}
                  placeholder="100000"
                  className="w-full bg-white/[0.03] border border-white/10 px-4 py-2.5 rounded-xl text-white text-[10px] font-bold focus:outline-none focus:border-blue-500 transition-all placeholder:text-gray-700 shadow-inner"
                />
                <p className="text-[8px] text-gray-600 ml-1 mt-1">Used to compute return % and drawdown</p>
              </div>

              {linkError && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-bold px-3 py-2 rounded-lg">
                  {linkError}
                </div>
              )}

              <div className="flex items-center gap-2 px-1 py-1">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-3 h-3 rounded border-white/10 bg-white/5 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                />
                <label htmlFor="rememberMe" className="text-[9px] font-black text-gray-400 uppercase tracking-widest cursor-pointer select-none">Remember</label>
              </div>

              <button disabled={isLinking} type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3.5 rounded-xl font-black text-[10px] tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 mt-2 active:scale-[0.98]">
                {isLinking ? (
                  <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <>SHOW MY STATS <ArrowUpRight size={14} /></>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="bg-black/40 border border-white/10 rounded-[32px] p-6 shadow-2xl backdrop-blur-md relative overflow-hidden border-t-blue-500/20">
              <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-[9px] font-black tracking-widest uppercase text-gray-500">Strategy</h3>
                  <p className="text-[8px] font-bold text-blue-500 mt-0.5 uppercase">Live Master EA</p>
                </div>
                <div className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-[8px] font-black text-blue-400">XAU</div>
              </div>
              <div className="h-[280px]">
                <TradingViewAnalysisWidget activeSignal={activeSignal} masterStats={masterStats} />
              </div>
            </div>

            {/* Setup */}
            <div className="bg-black/40 border border-white/10 rounded-[32px] p-6 shadow-2xl backdrop-blur-md">
              <div className="flex justify-between items-center mb-5">
                <div>
                  <h3 className="text-[9px] font-black tracking-widest uppercase text-gray-500">Setup</h3>
                  <p className="text-[8px] font-bold text-blue-500 mt-0.5 uppercase">Active Strategy</p>
                </div>
                <div className="px-2 py-0.5 bg-green-500/10 border border-green-500/20 rounded text-[8px] font-black text-green-400 flex items-center gap-1">
                  <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div>LIVE
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">Symbol</span>
                  <span className="text-[10px] font-black text-white">XAUUSD</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">Timeframes</span>
                  <span className="text-[10px] font-black text-white">5M / 15M</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">Setups</span>
                  <span className="text-[10px] font-black text-white">BB Bounce + EMA Pullback</span>
                </div>
              </div>
            </div>

            {isLinked && (
              <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-4 shadow-xl">
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-[10px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all shadow-lg active:scale-95"
                >
                  <LogOut size={14} /> Disconnect Broker
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      <main className="flex-1 flex flex-col gap-10 min-h-full pb-10">
        {activeTab === 'Account' ? (
          <div className="flex flex-col gap-10">
            {/* Account Tab Content */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-600/10 border border-blue-500/20 p-6 rounded-[32px] flex flex-col md:flex-row items-center justify-between gap-6 backdrop-blur-md"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${isLinked ? 'bg-green-500/20 text-green-500' : 'bg-blue-600/20 text-blue-500'} rounded-xl flex items-center justify-center`}>
                  {isLinked ? <Activity size={24} /> : <Shield size={24} />}
                </div>
                <div>
                  <h4 className="text-lg font-black text-white uppercase tracking-tight">
                    {isLinked ? (accountInfo.isSimulated ? 'System Local-Mode' : 'System Online') : 'Account Not Linked'}
                  </h4>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                    {isLinked && accountInfo.isSimulated ? (
                      <a href="https://cors-anywhere.herokuapp.com/corsdemo" target="_blank" rel="noopener noreferrer" className="text-yellow-500 animate-pulse flex items-center gap-2 hover:text-white transition-colors">
                        <AlertTriangle size={12} /> Click here and press 'Request temporary access' to activate live data.
                      </a>
                    ) : isLinked ? (
                      `Syncing with MetaTrader ID: ${accountInfo.id}`
                    ) : (
                      'Use your account number in the sidebar to link'
                    )}
                  </div>
                </div>
              </div>
              {isLinked && (
                <button 
                  onClick={handleLogout}
                  className="px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-[10px] font-black uppercase tracking-widest hover:bg-red-500/20 transition-all flex items-center gap-2"
                >
                  <Activity size={14} className="rotate-45" /> Disconnect Bridge
                </button>
              )}
              {!isLinked && (
                <div className="flex items-center gap-2 text-blue-400 text-[10px] font-black uppercase tracking-widest animate-pulse px-4 py-2 bg-blue-500/5 border border-blue-500/10 rounded-full">
                  <ArrowDownLeft size={14} /> Use sidebar to link account
                </div>
              )}
            </motion.div>

            {/* Top KPI Cards - Primary Metrics Only */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-10">
              {/* Total Balance Card */}
              <div className="bg-gradient-to-br from-blue-600/10 to-transparent border border-blue-500/20 rounded-2xl sm:rounded-[32px] p-5 sm:p-8 relative overflow-hidden group transition-all hover:bg-blue-600/20 shadow-2xl">
                <div className="flex justify-between items-start mb-4">
                  <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Total Balance</p>
                  <div className="p-2 bg-blue-600/20 rounded-lg text-blue-400 border border-blue-500/30 group-hover:scale-110 transition-transform shadow-inner"><Wallet size={14} /></div>
                </div>
                <h3 className="text-3xl font-black tracking-tighter text-white">
                  {isLinked ? `$${(accountInfo.balance || 0).toLocaleString()}` : '$0'}
                </h3>
                <p className="text-[10px] font-bold text-gray-500 mt-2 uppercase tracking-tight">{isLinked ? accountInfo.server : 'No Connection'}</p>
                <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <Wallet size={120} />
                </div>
              </div>

              {/* Total Result Card */}
              <div className="bg-gradient-to-br from-purple-600/10 to-transparent border border-purple-500/20 rounded-2xl sm:rounded-[32px] p-5 sm:p-8 relative overflow-hidden group transition-all hover:bg-purple-600/20 shadow-2xl">
                <div className="flex justify-between items-start mb-4">
                  <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Total Result</p>
                  <div className="p-2 bg-purple-600/20 rounded-lg text-purple-400 border border-purple-500/30 group-hover:scale-110 transition-transform shadow-inner"><Activity size={14} /></div>
                </div>
                <h3 className={`text-3xl font-black tracking-tighter ${(accountInfo.metrics?.totalProfit || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {isLinked ? `${(accountInfo.metrics?.totalProfit || 0) >= 0 ? '+' : ''}${(accountInfo.metrics?.totalProfit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0'}
                </h3>
                <p className={`text-[10px] font-bold ${(accountInfo.metrics?.totalProfit || 0) >= 0 ? 'text-green-500' : 'text-red-500'} mt-2 uppercase tracking-widest`}>
                  {isLinked ? `${(((accountInfo.metrics?.totalProfit || 0) / (accountInfo.balance || 1)) * 100).toFixed(2)}%` : '0.00%'}
                </p>
                <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <TrendingUp size={120} />
                </div>
              </div>

              {/* Bridge Status Card */}
              <div className={`${isLinked ? 'bg-blue-600 shadow-[0_20px_40px_rgba(37,99,235,0.3)]' : 'bg-white/5'} border ${isLinked ? 'border-white/20' : 'border-white/10'} rounded-2xl sm:rounded-[32px] p-5 sm:p-8 shadow-2xl transition-all relative overflow-hidden group`}>
                <div className="relative z-10">
                  <p className={`text-[10px] font-black ${isLinked ? 'text-blue-100' : 'text-gray-500'} uppercase tracking-widest mb-1`}>Bridge Status</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className={`w-2 h-2 rounded-full ${isLinked ? 'bg-green-400 animate-pulse shadow-[0_0_12px_#4ade80]' : 'bg-gray-700'}`} />
                    <h3 className="text-2xl font-black uppercase tracking-tighter text-white">
                      {isLinked ? 'CONNECTED' : 'OFFLINE'}
                    </h3>
                  </div>
                  <p className={`text-[10px] font-bold ${isLinked ? 'text-blue-100/70' : 'text-gray-400'} italic mt-1 tracking-widest`}>{isLinked ? `ID: ${accountInfo.id}` : 'Link Required'}</p>
                </div>
                {isLinked && (
                  <div className="absolute -right-6 -bottom-6 opacity-20 group-hover:scale-110 transition-transform group-hover:rotate-12 duration-700">
                    <Activity size={140} className="text-white" />
                  </div>
                )}
                {!isLinked && (
                   <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Shield size={120} />
                  </div>
                )}
              </div>
            </div>

            {/* Consolidated Audit & Performance Panel */}
            <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[40px] p-4 sm:p-6 md:p-10 relative overflow-hidden shadow-2xl">
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 md:gap-12">
                {/* Financials Group */}
                <div className="space-y-4 sm:space-y-6 bg-blue-600/5 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-blue-500/10">
                  <p className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] border-b border-blue-500/10 pb-2 flex items-center gap-2">
                    <DollarSign size={12} /> Financials
                  </p>
                  <div className="space-y-5">
                    {[
                      { label: 'Total Result', val: isLinked ? `$${(accountInfo.metrics?.totalProfit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0', sub: 'Net Profit/Loss', color: (accountInfo.metrics?.totalProfit || 0) >= 0 ? 'text-green-500' : 'text-red-500' },
                      { label: 'Deposits', val: isLinked ? `$${(accountInfo.metrics?.deposits || accountInfo.balance || 0).toLocaleString()}` : '$0', sub: 'Verified Protocol', color: 'text-white' },
                      { label: 'Withdrawals', val: isLinked ? `$${(accountInfo.metrics?.withdrawals || 0).toLocaleString()}` : '$0', sub: 'Total Outflow', color: 'text-white' }
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-[9px] font-bold text-gray-500 uppercase mb-1">{item.label}</p>
                        <p className={`text-xl font-black tracking-tight ${item.color || 'text-white'}`}>{item.val}</p>
                        <p className="text-[8px] font-bold text-gray-600 uppercase mt-0.5">{item.sub}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Performance Group */}
                <div className="space-y-4 sm:space-y-6 bg-purple-600/5 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-purple-500/10">
                  <p className="text-[10px] font-black text-purple-400 uppercase tracking-[0.2em] border-b border-purple-500/10 pb-2 flex items-center gap-2">
                    <Activity size={12} /> Performance
                  </p>
                  <div className="space-y-5">
                    {[
                      { label: 'Drawdown', val: isLinked ? `${Math.min(accountInfo.metrics?.maxDrawdown || 0, 100).toFixed(2)}%` : '0.00%', sub: 'Risk Exposure', color: 'text-white' },
                      { label: 'Win Rate', val: isLinked ? `${(accountInfo.metrics?.winRate || 0).toFixed(1)}%` : '0%', sub: 'Accuracy', color: 'text-blue-500' },
                      { label: 'Profit Factor', val: isLinked ? (accountInfo.metrics?.profitFactor || 0).toFixed(2) : '0.00', sub: 'Efficiency', color: 'text-white' },
                      { label: 'Total Orders', val: isLinked ? (accountInfo.metrics?.totalTrades || 0).toString() : '0', sub: 'Verified Trades', color: 'text-white' }
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-[9px] font-bold text-gray-500 uppercase mb-1">{item.label}</p>
                        <p className={`text-xl font-black tracking-tight ${item.color || 'text-white'}`}>{item.val}</p>
                        <p className="text-[8px] font-bold text-gray-600 uppercase mt-0.5">{item.sub}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Averages Group */}
                <div className="space-y-4 sm:space-y-6 bg-green-600/5 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-green-500/10">
                  <p className="text-[10px] font-black text-green-400 uppercase tracking-[0.2em] border-b border-green-500/10 pb-2 flex items-center gap-2">
                    <TrendingUp size={12} /> Averages
                  </p>
                  <div className="space-y-5">
                    {[
                      { label: 'Average Win', val: isLinked ? `$${(accountInfo.metrics?.averageWin || 0).toFixed(2)}` : '$0', sub: 'Per Trade', color: 'text-green-500' },
                      { label: 'Average Loss', val: isLinked ? `$${(accountInfo.metrics?.averageLoss || 0).toFixed(2)}` : '$0', sub: 'Per Trade', color: 'text-red-500' }
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-[9px] font-bold text-gray-500 uppercase mb-1">{item.label}</p>
                        <p className={`text-xl font-black tracking-tight ${item.color || 'text-white'}`}>{item.val}</p>
                        <p className="text-[8px] font-bold text-gray-600 uppercase mt-0.5">{item.sub}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live Status Group */}
                <div className="space-y-4 sm:space-y-6 bg-yellow-600/5 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-yellow-500/10">
                  <p className="text-[10px] font-black text-yellow-400 uppercase tracking-[0.2em] border-b border-yellow-500/10 pb-2 flex items-center gap-2">
                    <Zap size={12} /> Live Status
                  </p>
                  <div className="space-y-5">
                    {[
                      { label: 'Equity', val: isLinked ? `$${(accountInfo.equity || 0).toLocaleString()}` : '$0', sub: 'Live Equity Sync', color: 'text-white' },
                      { label: 'Floating P/L', val: isLinked ? `$${(accountInfo.profit || 0).toFixed(2)}` : '$0', sub: `${isLinked ? (accountInfo.trades || []).length : 0} active orders`, color: (accountInfo.profit || 0) >= 0 ? 'text-green-500' : 'text-red-500' }
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-[9px] font-bold text-gray-500 uppercase mb-1">{item.label}</p>
                        <p className={`text-xl font-black tracking-tight ${item.color || 'text-white'}`}>{item.val}</p>
                        <p className="text-[8px] font-bold text-gray-600 uppercase mt-0.5">{item.sub}</p>
                      </div>
                    ))}
                    <div className="pt-2">
                      <p className="text-[8px] font-black text-gray-700 uppercase tracking-[0.2em]">Updated: {isLinked ? 'Just Now' : 'Never'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Growth Analysis & Daily Performance Section */}
            <div className="grid lg:grid-cols-12 gap-4 sm:gap-10">
              <div className="lg:col-span-8">
                <AccountHistoryChart isLinked={isLinked} dailyGrowth={accountInfo.metrics?.dailyGrowth} />
              </div>
              <div className="lg:col-span-4">
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl sm:rounded-[40px] h-full relative overflow-hidden flex flex-col group transition-all hover:bg-white/[0.05] shadow-2xl">
                  {/* Premium glass effect header */}
                  <div className="p-4 sm:p-6 md:p-8 pb-4 sm:pb-6 border-b border-white/5 bg-white/[0.02] backdrop-blur-md">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="text-xl font-black tracking-tight text-white uppercase">Daily Pulse</h3>
                      <div className="flex items-center gap-2">
                        <button className="p-1.5 rounded-lg bg-white/5 hover:bg-blue-600/20 text-gray-500 hover:text-blue-400 transition-all border border-white/5"><ChevronLeft size={14} /></button>
                        <button className="p-1.5 rounded-lg bg-white/5 hover:bg-blue-600/20 text-gray-500 hover:text-blue-400 transition-all border border-white/5"><ChevronRight size={14} /></button>
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <p className="text-[9px] font-bold text-gray-500 uppercase tracking-[0.2em]">Protocol Weekly View</p>
                      <span className="text-[8px] font-black text-blue-500 uppercase bg-blue-500/10 px-2 py-0.5 rounded">WEEK 02</span>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-hidden">
                    {/* Header Row */}
                    <div className="grid grid-cols-3 text-[9px] sm:text-[10px] font-black text-gray-500 uppercase tracking-widest px-3 sm:px-6 md:px-8 py-3 sm:py-4 border-b border-white/5 bg-[#050505]/95">
                      <span>Date</span>
                      <span className="text-center">Drawdown</span>
                      <span className="text-right">Result</span>
                    </div>
                    
                    <div className="px-4 py-2">
                      {isLinked ? (
                        (() => {
                          const growthMap = new Map();
                          (accountInfo.metrics?.dailyGrowth || []).forEach(d => {
                            const dateStr = new Date(d.date).toISOString().split('T')[0];
                            growthMap.set(dateStr, d);
                          });

                          return Array.from({ length: 7 }).map((_, i) => {
                            const d = new Date();
                            d.setDate(d.getDate() - i);
                            const dateKey = d.toISOString().split('T')[0];
                            const dayData = growthMap.get(dateKey) || { 
                              date: d.toISOString(), 
                              profit: 0, 
                              profitPercentage: 0, 
                              drawdown: 0 
                            };

                            return (
                              <div key={i} className="grid grid-cols-3 items-center px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl hover:bg-white/[0.03] transition-all duration-300 group/row">
                                <div className="flex items-center gap-3">
                                  <div className="w-1 h-4 rounded-full bg-blue-600/20 group-hover/row:bg-blue-500 transition-colors"></div>
                                  <span className="text-[11px] font-black text-white/80 group-hover/row:text-white transition-colors">
                                    {new Date(dayData.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                  </span>
                                </div>
                                
                                <div className="flex flex-col items-center">
                                  <span className="text-[10px] font-bold text-white group-hover/row:text-white transition-colors">
                                    {(dayData.drawdown || 0).toFixed(2)}%
                                  </span>
                                </div>
                                
                                <div className="text-right">
                                  <p className={`text-[11px] font-black ${(dayData.profit || 0) >= 0 ? 'text-green-500' : 'text-red-500'} group-hover/row:brightness-110 leading-none`}>
                                    ${(dayData.profit || 0).toFixed(2)}
                                  </p>
                                  <p className={`text-[9px] font-bold ${(dayData.profitPercentage || 0) >= 0 ? 'text-green-500/70' : 'text-red-500/70'} mt-1 uppercase tracking-tighter`}>
                                    {(dayData.profitPercentage || 0) >= 0 ? '+' : ''}{(dayData.profitPercentage || 0).toFixed(2)}%
                                  </p>
                                </div>
                              </div>
                            );
                          });
                        })()
                      ) : (
                        Array.from({ length: 7 }).map((_, i) => {
                          const d = new Date();
                          d.setDate(d.getDate() - i);
                          return {
                            date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
                            dd: '0.00%',
                            res: '$0',
                            resPct: '0.00%'
                          };
                        }).map((day, i) => (
                          <div key={i} className="grid grid-cols-3 items-center px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl hover:bg-white/[0.03] transition-all duration-300 group/row">
                            <div className="flex items-center gap-3">
                              <div className="w-1 h-4 rounded-full bg-blue-600/20 group-hover/row:bg-blue-500 transition-colors"></div>
                              <span className="text-[11px] font-black text-white/80 group-hover/row:text-white transition-colors">{day.date}</span>
                            </div>
                            
                            <div className="flex flex-col items-center">
                              <span className="text-[10px] font-bold text-white group-hover/row:text-white transition-colors">{day.dd}</span>
                            </div>
                            
                            <div className="text-right">
                              <p className={`text-[11px] font-black ${parseFloat(day.res.replace('$', '')) >= 0 ? 'text-green-500' : 'text-red-500'} group-hover/row:brightness-110 leading-none`}>{day.res}</p>
                              <p className={`text-[9px] font-bold ${parseFloat(day.resPct) >= 0 ? 'text-green-500/70' : 'text-red-500/70'} mt-1 uppercase tracking-tighter`}>{parseFloat(day.resPct) >= 0 ? '+' : ''}{day.resPct}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Bottom Stats Footer */}
                  <div className="p-6 bg-blue-600/5 border-t border-white/5 flex items-center justify-between">
                    <p className="text-[9px] font-black text-blue-500/50 uppercase tracking-[0.3em]">Institutional Feed</p>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500/30"></div>
                      <p className="text-[9px] font-bold text-gray-600 uppercase">Live Sync</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-10">
              {/* Open Positions Section */}
              <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[40px] p-4 sm:p-6 md:p-10 relative overflow-hidden">
                <div className="flex justify-between items-center mb-6 sm:mb-10">
                  <div>
                    <h3 className="text-lg sm:text-2xl font-black tracking-tight text-white uppercase">{isLinked ? 'Open Positions' : 'Live Signal Bridge'}</h3>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">{isLinked ? 'Your current active trades' : 'Global execution pool'}</p>
                  </div>
                  {isLinked && (
                    <div className="px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-500 text-[9px] font-black uppercase tracking-widest flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                      Live Syncing
                    </div>
                  )}
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-white/5 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">
                        <th className="pb-6">Asset Pair</th>
                        <th className="pb-6">Position</th>
                        <th className="pb-6">Entry</th>
                        <th className="pb-6">Current</th>
                        <th className="pb-6">PnL (USD)</th>
                        <th className="pb-6">Size</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm font-bold text-gray-300">
                      {isLinked ? (
                        accountInfo.trades.length > 0 ? (
                          accountInfo.trades
                            .sort((a, b) => new Date(b.time) - new Date(a.time))
                            .map((trade, i) => (
                              <tr key={i} className="group hover:bg-white/[0.02] transition-colors border-b border-white/5 last:border-0">
                                <td className="py-6 flex items-center gap-3 text-white">
                                  <div className="w-8 h-8 rounded-lg bg-blue-600/20 text-blue-400 flex items-center justify-center font-black text-xs">{trade.pair?.substring(0, 1)}</div>
                                  {trade.pair}
                                </td>
                                <td className="py-6">
                                  <span className={`px-2 py-1 rounded text-[10px] ${trade.type === 'BUY' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>{trade.type}</span>
                                </td>
                                <td className="py-6 font-mono text-xs text-white">{trade.entry}</td>
                                <td className="py-6 font-mono text-xs text-white">{trade.current}</td>
                                <td className={`py-6 font-mono text-xs ${parseFloat(trade.pnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {parseFloat(trade.pnl) >= 0 ? '+' : ''}{trade.pnl}
                                </td>
                                <td className="py-6 font-mono text-xs text-gray-500">{trade.size}</td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="py-10 text-center text-[10px] font-bold text-gray-600 uppercase tracking-widest">No active positions found</td>
                          </tr>
                        )
                      ) : (
                        <tr>
                          <td colSpan="6" className="py-20 text-center">
                            <div className="flex flex-col items-center gap-4">
                              <Activity size={32} className="text-gray-700 animate-pulse" />
                              <p className="text-gray-600 font-bold uppercase tracking-[0.3em] text-[10px]">Awaiting bridge connection</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Closed Trades Section */}
              <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[40px] p-4 sm:p-6 md:p-10 relative overflow-hidden">
                <div className="flex justify-between items-center mb-6 sm:mb-10">
                  <div>
                    <h3 className="text-lg sm:text-2xl font-black tracking-tight text-white uppercase">Trading History</h3>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">Recently closed deals and performance</p>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-white/5 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">
                        <th className="pb-6">Date</th>
                        <th className="pb-6">Asset</th>
                        <th className="pb-6">Type</th>
                        <th className="pb-6">Result (USD)</th>
                        <th className="pb-6">Outcome</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm font-bold text-gray-300">
                      {isLinked && accountInfo.metrics?.trades?.length > 0 ? (
                        [...accountInfo.metrics.trades]
                          .sort((a, b) => new Date(b.time) - new Date(a.time))
                          .map((trade, i) => {
                            const tradeDate = new Date(trade.time);
                            const isToday = tradeDate.toDateString() === new Date().toDateString();
                            
                            return (
                              <tr key={i} className={`group hover:bg-white/[0.02] transition-colors border-b border-white/5 last:border-0 ${isToday ? 'bg-blue-500/[0.02]' : ''}`}>
                                <td className="py-6 text-[11px] font-mono text-gray-500">
                                  <div className="flex flex-col">
                                    <span className={isToday ? 'text-blue-400 font-black' : ''}>
                                      {tradeDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                    </span>
                                    <span className="text-[9px] opacity-50">
                                      {tradeDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                </td>
                                <td className="py-6 flex items-center gap-3 text-white">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs ${isToday ? 'bg-blue-600/30 text-blue-300' : 'bg-gray-600/20 text-gray-400'}`}>
                                    {(trade.symbol || 'X').substring(0, 1)}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="font-black tracking-tight">{trade.symbol}</span>
                                    {isToday && <span className="text-[7px] text-blue-500 font-black uppercase tracking-widest">New Today</span>}
                                  </div>
                                </td>
                                <td className="py-6">
                                  <span className={`px-2 py-1 rounded text-[10px] ${(trade.profit || 0) >= 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                                    {trade.type}
                                  </span>
                                </td>
                                <td className={`py-6 font-mono text-xs ${(trade.profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {(trade.profit || 0) >= 0 ? '+' : ''}{(trade.profit || 0).toFixed(2)}
                                </td>
                                <td className="py-6">
                                  <span className={`text-[10px] font-black uppercase tracking-widest ${(trade.profit || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {(trade.profit || 0) >= 0 ? 'PROFIT' : 'LOSS'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td colSpan="5" className="py-20 text-center text-[10px] font-bold text-gray-600 uppercase tracking-widest">
                            {isLinked ? 'No history available yet' : 'Connect account to view history'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-10">
            <RiskGuardWidget />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 lg:gap-10">
              <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[32px] p-4 sm:p-8 relative overflow-hidden group transition-all hover:bg-white/[0.07]">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">Master Status</p>
                <h3 className="text-xl sm:text-3xl font-black mb-2 tracking-tighter uppercase text-white">{activeSignal ? 'IN TRADE' : 'STANDBY'}</h3>
                <p className="text-[10px] font-bold text-green-500 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>Live Connection</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[32px] p-4 sm:p-8 relative overflow-hidden group transition-all hover:bg-white/[0.07]">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">Drawdown</p>
                <h3 className="text-xl sm:text-3xl font-black mb-2 tracking-tighter text-red-500 tabular-nums">
                  {isLinked ? `${Math.min(accountInfo.metrics?.maxDrawdown || 0, 100).toFixed(2)}%` : (masterStats ? `${masterStats.maxDrawdown.toFixed(2)}%` : '—')}
                </h3>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">{isLinked ? 'Current Live Risk' : 'Master Account'}</p>
              </div>
              <div className="bg-blue-600 border border-blue-400/30 rounded-2xl sm:rounded-[32px] p-4 sm:p-8 shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform"><TrendingUp size={48} /></div>
                <p className="text-[10px] font-black text-blue-100 uppercase tracking-widest mb-1 opacity-80">Win Rate</p>
                <h3 className="text-xl sm:text-3xl font-black mb-2 tracking-tighter text-white tabular-nums">
                  {isLinked && accountInfo.metrics?.winRate ? `${accountInfo.metrics.winRate.toFixed(1)}%` : (masterStats ? `${masterStats.winRate.toFixed(1)}%` : '—')}
                </h3>
                <p className="text-[10px] font-bold text-blue-100 italic">{isLinked ? 'Live Account Data' : `${masterStats?.tradeCount || 0} Trades Tracked`}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[32px] p-4 sm:p-8 relative overflow-hidden group transition-all hover:bg-white/[0.07]">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">Total P/L</p>
                <h3 className={`text-xl sm:text-3xl font-black mb-2 tracking-tighter tabular-nums ${(masterStats?.totalProfit || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {isLinked && accountInfo.metrics?.totalProfit !== undefined ?
                    (accountInfo.metrics.totalProfit >= 0 ? `+$${accountInfo.metrics.totalProfit.toFixed(2)}` : `-$${Math.abs(accountInfo.metrics.totalProfit).toFixed(2)}`)
                    : (masterStats ? `${masterStats.totalProfit >= 0 ? '+' : '-'}$${Math.abs(masterStats.totalProfit).toFixed(0)}` : '—')}
                </h3>
                <p className="text-[10px] font-bold text-green-500 uppercase tracking-widest">{isLinked ? 'Verified Result' : 'All Time Verified'}</p>
              </div>
            </div>

            {/* Main Sections */}
            <div className="grid lg:grid-cols-12 gap-4 sm:gap-10 items-start">
              <div className="lg:col-span-8 bg-white/5 border border-white/10 rounded-2xl sm:rounded-[40px] p-4 sm:p-8 pb-2 relative overflow-hidden">
                <div className="flex justify-between items-center mb-6 relative z-10 text-white">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-600/10 p-2 rounded-xl border border-blue-500/20"><TrendingUp className="text-blue-400" size={18} /></div>
                    <div><h3 className="text-xl font-black tracking-tight text-white uppercase">Master Execution</h3><p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">Live Global Spot Price (XAU/USD)</p></div>
                  </div>
                  <div className="px-3 py-1 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div><span className="text-[10px] font-black text-green-500 uppercase tracking-widest">Live Feed</span></div>
                </div>
                <div className="h-[400px] w-full relative group">
                  <TradingViewWidget />
                  <AnimatePresence>
                    {isInitialAnalysis && (
                      <><motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-20 pointer-events-none bg-blue-500/5 backdrop-blur-[1px]" />
                        <motion.div initial={{ left: "-5%" }} animate={{ left: "105%" }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }} className="absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-blue-400 to-transparent z-30 pointer-events-none" />
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute top-4 left-1/2 -translate-x-1/2 z-40 bg-black/40 border border-blue-500/20 px-6 py-2 rounded-full backdrop-blur-md flex items-center gap-3"><div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse shadow-[0_0_10px_#3b82f6]" /><span className="text-blue-400 text-[9px] font-black uppercase tracking-[0.4em]">Neural Scanning</span></motion.div></>
                    )}
                  </AnimatePresence>
                  <div className="absolute bottom-12 left-6 flex flex-col gap-4 pointer-events-none sm:pointer-events-auto z-30">
                    <motion.div layout className="bg-black/80 border border-white/10 p-3 sm:p-4 rounded-2xl backdrop-blur-xl shadow-xl min-w-[180px] sm:min-w-[240px]">
                      {activeSignal ? (
                        <>
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-[7px] font-black text-blue-400 uppercase tracking-widest mb-0.5">Active Master Trade</p>
                              <h4 className={`text-[14px] font-black uppercase ${activeSignal.direction === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>{activeSignal.direction} XAUUSD</h4>
                            </div>
                            <div className="px-1.5 py-0.5 border rounded bg-green-500/10 text-green-500 border-green-500/20 flex items-center gap-1">
                              <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div>
                              <span className="text-[8px] font-black uppercase">Live</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {activeSignal.entry_price && (
                              <div className="flex justify-between text-[9px] font-bold border-b border-white/5 pb-1.5"><span className="text-gray-500 uppercase">Entry</span><span className="text-white tabular-nums">{activeSignal.entry_price.toFixed(2)}</span></div>
                            )}
                            <div className="flex justify-between text-[9px] font-bold border-b border-white/5 pb-1.5"><span className="text-green-400 uppercase">Target</span><span className="text-green-400 tabular-nums">{(activeSignal.tp || 0).toFixed(2)}</span></div>
                            <div className="flex justify-between text-[9px] font-bold"><span className="text-red-400 uppercase">Stop Loss</span><span className="text-red-400 tabular-nums">{(activeSignal.sl || 0).toFixed(2)}</span></div>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center py-2">
                          <p className="text-[7px] font-black text-blue-400 uppercase tracking-widest mb-1">Master Account</p>
                          <h4 className="text-[14px] font-black uppercase text-gray-400">No Active Trade</h4>
                          <p className="text-[8px] font-bold text-gray-600 italic mt-1">Waiting for next setup</p>
                        </div>
                      )}
                    </motion.div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-10">
                <div className="bg-white rounded-[32px] p-6 shadow-2xl relative overflow-hidden flex flex-col group border border-gray-100 transition-all">
                  <div className="absolute top-0 left-0 w-full h-1.5 bg-blue-600"></div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100"><Cpu size={16} /></div>
                    <div><h3 className="text-lg font-black tracking-tighter leading-none text-black uppercase">FlexBot AI License</h3><p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">Official Protocol V5.0</p></div>
                  </div>
                  <div className="space-y-4">
                    <div className="text-center py-6 bg-gray-50 rounded-2xl border border-gray-100 relative overflow-hidden">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1.5">Lifetime Access</p>
                      <div className="flex items-baseline justify-center gap-2 mb-1"><h4 className="text-4xl font-black tracking-tighter text-black">$500</h4><span className="text-gray-300 text-sm font-bold line-through">$700</span></div>
                      <div className="mt-3 pt-3 border-t border-gray-100 w-full px-4"><p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Ongoing Access</p><p className="text-base font-black text-blue-600">$30<span className="text-[10px] opacity-60 ml-1">/ MONTH</span></p></div>
                    </div>
                    <div className="space-y-1.5">
                      {["FlexBot AI V5.0 Core", "Lifetime Protocol Access", "MT5 Signal Bridge", "24/7 Deployment Support"].map((item, i) => (
                        <div key={i} className="flex items-center gap-2.5 px-3 py-2 bg-white rounded-xl border border-gray-100 shadow-sm"><div className="w-1 h-1 rounded-full bg-blue-600" /><span className="text-[9px] font-bold text-gray-500 uppercase tracking-tight">{item}</span></div>
                      ))}
                    </div>
                    <button onClick={onBuyClick} className="w-full bg-black hover:bg-blue-600 text-white py-4 rounded-xl font-black text-sm transition-all shadow-xl flex items-center justify-center gap-3 group/btn uppercase tracking-widest">Activate FlexBot AI<ArrowUpRight size={16} className="group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" /></button>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-start gap-2.5"><Shield size={14} className="text-gray-400 mt-0.5" /><p className="text-[8px] font-bold text-gray-400 leading-relaxed uppercase tracking-tight">Secure non-custodial software. 100% user-controlled capital.</p></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trades Table */}
            <div className="grid lg:grid-cols-12 gap-4 sm:gap-10 pb-10">
              <div className="lg:col-span-8 bg-white/5 border border-white/10 rounded-2xl sm:rounded-[40px] p-4 sm:p-6 md:p-10">
                <h3 className="text-2xl font-black tracking-tight text-white mb-10">{isLinked ? 'My MetaTrader Trades' : 'Active AI Trades'}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead><tr className="border-b border-white/5 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]"><th className="pb-6">Asset Pair</th><th className="pb-6">Position</th><th className="pb-6">Entry Price</th><th className="pb-6">Current</th><th className="pb-6">PnL (USD)</th><th className="pb-6">Status</th></tr></thead>
                    <tbody className="text-sm font-bold text-gray-300">
                      {isLinked && accountInfo.trades.map((trade, i) => (
                        <tr key={i} className="group hover:bg-white/[0.02] transition-colors border-b border-white/5 last:border-0">
                          <td className="py-6 flex items-center gap-3 text-white"><div className="w-8 h-8 bg-blue-600/20 rounded-lg flex items-center justify-center text-blue-400 font-black text-xs">X</div>{trade.pair}</td>
                          <td className="py-6"><span className={`px-2 py-1 rounded text-[10px] ${(trade.side || trade.type) === 'LONG' || (trade.side || trade.type) === 'BUY' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>{trade.side || trade.type}</span></td>
                          <td className="py-6 font-mono text-xs text-white">{trade.entry}</td>
                          <td className="py-6 font-mono text-xs text-white">{trade.current}</td>
                          <td className={`py-6 font-mono text-xs ${trade.pnl.toString().startsWith('+') || parseFloat(trade.pnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{trade.pnl.toString().startsWith('+') || parseFloat(trade.pnl) >= 0 ? '+' : ''}{trade.pnl}</td>
                          <td className="py-6"><div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div><span className="text-xs text-gray-500 uppercase">{trade.status}</span></div></td>
                        </tr>
                      ))}
                      {!isLinked && activeSignal && (
                        <tr className="group hover:bg-white/[0.02] transition-colors">
                          <td className="py-6 flex items-center gap-3 text-white"><div className="w-8 h-8 bg-blue-600/20 rounded-lg flex items-center justify-center text-blue-400 font-black text-xs">XAU</div>{activeSignal.symbol}</td>
                          <td className="py-6"><span className={`px-2 py-1 rounded text-[10px] ${activeSignal.direction === 'BUY' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>{activeSignal.direction}</span></td>
                          <td className="py-6 font-mono text-xs text-white tabular-nums">{activeSignal.entry_price ? activeSignal.entry_price.toFixed(2) : '—'}</td>
                          <td className="py-6 font-mono text-xs text-gray-500 tabular-nums">TP {(activeSignal.tp || 0).toFixed(2)}</td>
                          <td className="py-6 font-mono text-xs text-red-400 tabular-nums">SL {(activeSignal.sl || 0).toFixed(2)}</td>
                          <td className="py-6"><div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div><span className="text-xs text-green-500 uppercase">Active</span></div></td>
                        </tr>
                      )}
                      {!isLinked && !activeSignal && (
                        <tr>
                          <td colSpan="6" className="py-20 text-center text-[10px] font-bold text-gray-600 uppercase tracking-widest">No active master trade — waiting for next setup</td>
                        </tr>
                      )}
                      {isLinked && accountInfo.trades.length === 0 && (
                        <tr>
                          <td colSpan="6" className="py-10 text-center text-[10px] font-bold text-gray-600 uppercase tracking-widest">No active positions found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="lg:col-span-4 flex flex-col">
            <div className="bg-[#111] border border-white/5 rounded-2xl sm:rounded-[40px] p-4 sm:p-8 flex-1 flex flex-col">
              <div className="flex justify-between items-center mb-8"><h3 className="text-xs font-black tracking-widest uppercase text-gray-500">Terminal Log</h3><div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div></div>
              <div className="flex-1 font-mono text-[12px] space-y-3 overflow-hidden">
                {tradingLogs.slice(0, 8).map((log, i) => (
                  <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} key={i} className={`p-3 rounded-xl border-l-2 ${log.type === 'TRADE' ? 'bg-blue-600/10 border-blue-500' : 'bg-white/[0.03] border-white/10'}`}>
                    <div className="flex justify-between items-center text-[9px] opacity-70 font-bold uppercase tracking-widest mb-1.5"><span className="text-gray-300">{log.type}</span><span className="text-blue-400">{log.meta}</span></div>
                    <div className={`leading-snug ${log.type === 'TRADE' ? 'text-white font-bold' : 'text-gray-400'}`}>{log.msg}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// --- Page: Protocol ---
const ContractPage = ({ onBuyClick }) => {
  return (
    <div className="relative min-h-screen py-12 sm:py-24 px-4 sm:px-6">
      <SEO
        title="How It Works — FlexBot AI Expert Advisor Installation"
        description="Learn how FlexBot AI works: install the Expert Advisor on your MT5, connect to your own broker, and the AI copies institutional gold trades automatically."
        path="/how-it-works"
      />
      <div className="absolute inset-0 bg-blue-600/[0.02] pointer-events-none"></div>
      <div className="container mx-auto max-w-6xl relative z-10">

        {/* Hero Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12 sm:mb-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/5 border border-blue-500/10 text-blue-500 text-[10px] font-black uppercase tracking-widest mb-6">
                <Cpu size={12} /> V5.0 Protocol
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tighter mb-5 text-white uppercase leading-[1.1]">How FlexBot <br/><span className="text-blue-500">AI</span> Works</h1>
              <p className="text-gray-400 font-medium leading-relaxed text-sm sm:text-base">FlexBot is an Expert Advisor that automatically copies our trades to your MetaTrader account. Your funds always stay with your own broker.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { value: '0.5%', label: 'Risk per trade', bg: 'bg-blue-500/5 border-blue-500/10' },
                { value: '1', label: 'Max open trades', bg: 'bg-purple-500/5 border-purple-500/10' },
                { value: '5%', label: 'Max drawdown', bg: 'bg-red-500/5 border-red-500/10' },
                { value: '24/5', label: 'Fully automated', bg: 'bg-green-500/5 border-green-500/10' },
              ].map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 * i }}
                  className={`${s.bg} border rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-center`}>
                  <p className="text-2xl sm:text-3xl font-black text-white mb-1">{s.value}</p>
                  <p className="text-[8px] sm:text-[9px] font-black text-gray-500 uppercase tracking-widest">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Installation flow */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white/[0.03] border border-white/10 rounded-2xl sm:rounded-[48px] overflow-hidden shadow-2xl mb-6 sm:mb-10">

          {/* Header */}
          <div className="p-6 sm:p-10 md:p-14 text-center border-b border-white/5">
            <div className="text-[10px] font-black text-blue-500 uppercase tracking-[0.3em] mb-3">Setup</div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-3">After Payment, You're Live in Under a Minute</h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
              Once your USDT payment is confirmed, you get a private download link. Unzip the file, run the installer, and FlexBot is set up on your MT5. No technical knowledge needed.
            </p>
          </div>

          {/* 3 steps */}
          <div className="grid lg:grid-cols-3">
            {[
              { step: '01', title: 'Receive Your Download', desc: "We send a private link with your personal FlexBot zip — license preset already baked in.", iconStyle: 'bg-blue-500/10 border-blue-500/20 text-blue-500', icon: <Download size={24} /> },
              { step: '02', title: 'Unzip the File', desc: 'Extract the zip anywhere on your PC. Inside is a single installer executable — no setup wizard, no choices to make.', iconStyle: 'bg-purple-500/10 border-purple-500/20 text-purple-500', icon: <Package size={24} /> },
              { step: '03', title: 'Run the Installer', desc: 'Double-click. The installer finds your MT5, copies the EA, compiles it, and saves your preset — all automatic. Done.', iconStyle: 'bg-green-500/10 border-green-500/20 text-green-500', icon: <Play size={24} /> },
            ].map((s, i) => (
              <div key={i} className={`p-6 sm:p-10 ${i < 2 ? 'border-b lg:border-b-0 lg:border-r border-white/5' : ''} group hover:bg-white/[0.03] transition-all`}>
                <div className={`w-12 h-12 rounded-2xl ${s.iconStyle} border flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  {s.icon}
                </div>
                <div className="text-[10px] font-black text-gray-600 uppercase tracking-[0.3em] mb-2">Step {s.step}</div>
                <h3 className="text-lg sm:text-xl font-black text-white uppercase tracking-tight mb-3">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Installer screenshot */}
          <div className="px-6 sm:px-10 md:px-14 pb-6 sm:pb-10 md:pb-14 pt-2 border-t border-white/5">
            <p className="text-center text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] mt-8 mb-6">What you see when you run it</p>
            <div className="relative mx-auto max-w-3xl rounded-2xl overflow-hidden border border-white/10 shadow-[0_25px_80px_-15px_rgba(0,0,0,0.7)] bg-black">
              {/* macOS-style window chrome */}
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-white/[0.03]">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/60"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/60"></div>
                </div>
                <p className="text-[9px] sm:text-[10px] font-bold text-gray-500 ml-2 tracking-wider">FlexBot Installer</p>
              </div>
              <img
                src="/installer-preview.jpg"
                alt="FlexBot installer running through setup steps"
                className="w-full h-auto block"
              />
            </div>
            <p className="text-center text-gray-500 text-xs sm:text-sm mt-5 leading-relaxed max-w-xl mx-auto">
              Five automated steps — find your MT5, install the EA, compile it, save your preset, you're done. Total time: under a minute.
            </p>
          </div>
        </motion.div>

        {/* Split: What it does + Safety */}
        <div className="grid lg:grid-cols-5 gap-6 sm:gap-10 mb-6 sm:mb-10">
          {/* Left: Features */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="lg:col-span-3 bg-white/[0.03] border border-white/10 rounded-2xl sm:rounded-[40px] p-5 sm:p-8 md:p-10 shadow-2xl">
            <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight mb-8">What FlexBot Does For You</h2>
            <div className="space-y-5">
              {[
                { icon: <Cpu size={18} />, title: 'Fully Automated', desc: 'No manual trading needed. Set it up once and FlexBot handles the rest. Runs 24 hours a day, 5 days a week.' },
                { icon: <Shield size={18} />, title: 'Your Funds, Your Broker', desc: 'We never touch your money. The EA runs on your own MetaTrader and your own broker account.' },
                { icon: <Clock size={18} />, title: 'News Filter', desc: 'FlexBot automatically stops trading during high-impact news events like NFP, FOMC and CPI releases.' },
                { icon: <Activity size={18} />, title: 'Daily Loss Protection', desc: 'When the maximum daily loss is reached, FlexBot automatically stops trading until the next day. Built for prop firm rules.' },
              ].map((f, i) => (
                <div key={i} className="flex gap-4 items-start group">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-500 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    {f.icon}
                  </div>
                  <div>
                    <h4 className="text-white font-black uppercase text-sm mb-1">{f.title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Prop Firm Compliance */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="lg:col-span-2 bg-blue-600 rounded-2xl sm:rounded-[40px] p-5 sm:p-8 md:p-10 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 opacity-10 pointer-events-none"><Shield size={200} /></div>
            <div className="relative z-10">
              <div className="text-[10px] font-black text-blue-200 uppercase tracking-[0.3em] mb-2">Compliant</div>
              <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight mb-6">Prop Firm Ready</h2>
              <p className="text-blue-100 text-sm leading-relaxed mb-8">FlexBot is built to pass FTMO and other prop firm challenges, and to keep funded accounts safe.</p>
              <div className="space-y-3">
                {[
                  'Max 0.5% risk per trade',
                  'Maximum 1 trade at a time',
                  'No weekend or overnight positions',
                  'Avoids news events automatically',
                  'Dynamic lot sizing per equity',
                  'Auto-stop at daily loss limit',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-white shrink-0"></div>
                    <span className="text-[11px] font-bold text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Pricing Banner */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white/[0.03] border border-white/10 rounded-2xl sm:rounded-[48px] overflow-hidden shadow-2xl mb-6 sm:mb-10">
          <div className="grid lg:grid-cols-2">
            <div className="p-6 sm:p-10 md:p-14 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-white/5">
              <div className="text-[10px] font-black text-blue-500 uppercase tracking-[0.3em] mb-3">Pricing</div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-5xl sm:text-6xl font-black text-white">$500</span>
                <span className="text-gray-600 font-bold line-through text-lg">$700</span>
              </div>
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-6">One-time + $30/month for AI connection</p>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">Lifetime license for FlexBot AI V5.0. Includes all updates, installation support, and access to our Telegram community.</p>
              <button onClick={onBuyClick} className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all shadow-[0_10px_50px_rgba(37,99,235,0.3)] flex items-center gap-3 w-fit">
                Get FlexBot AI <ArrowUpRight size={18} />
              </button>
            </div>
            <div className="p-6 sm:p-10 md:p-14 flex flex-col justify-center">
              <div className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] mb-6">What You Get</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'FlexBot AI V5.0 EA',
                  'Lifetime updates',
                  'MT5 support',
                  'Signal bridge',
                  'Prop firm mode',
                  'Telegram support 24/7',
                  'Installation guide',
                  'Lot sizing engine'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 py-2">
                    <div className="w-5 h-5 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    </div>
                    <span className="text-[11px] font-bold text-gray-300 uppercase tracking-tight">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/5 flex items-center gap-2">
                <Shield size={14} className="text-gray-600" />
                <p className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">Payment via USDT (BEP-20) on Binance Smart Chain</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Requirements - Horizontal */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white/[0.03] border border-white/10 rounded-2xl sm:rounded-[40px] p-5 sm:p-8 md:p-10 shadow-2xl">
          <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight mb-8 flex items-center gap-3">
            <Settings size={20} className="text-blue-500" /> Requirements
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[
              { title: 'MetaTrader 5', desc: 'Desktop version' },
              { title: 'Any broker with Gold', desc: 'Low spread recommended' },
              { title: 'Min. $1,000 balance', desc: 'For proper lot sizing' },
              { title: 'VPS (optional)', desc: 'For 24/5 uptime' },
              { title: 'Stable internet', desc: 'EA must stay running' },
              { title: '1:100 leverage', desc: 'Or higher recommended' },
            ].map((item, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl sm:rounded-2xl p-4 hover:bg-white/[0.05] transition-all">
                <h4 className="text-white font-black text-xs sm:text-sm uppercase mb-1">{item.title}</h4>
                <p className="text-gray-600 text-[10px] font-bold uppercase tracking-widest">{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </div>
  );
};

// --- Payment Modal ---
const PaymentModal = ({ isOpen, step, onSelect, onConfirm, onClose, selectedPlan, usdtAddress }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-xl">
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-[#0a0a0a] border border-white/10 rounded-t-3xl sm:rounded-[32px] w-full max-w-lg overflow-y-auto max-h-[92vh] shadow-2xl relative p-6 sm:p-8 text-center">
        <button onClick={onClose} className="absolute top-5 right-5 text-gray-500 hover:text-white transition-colors z-10"><X size={22} /></button>
        {step === 'select' && (
          <>
            <div className="w-14 h-14 bg-blue-600/10 rounded-2xl flex items-center justify-center text-blue-500 mx-auto mb-4"><Cpu size={26} /></div>
            <h3 className="text-2xl font-black tracking-tighter mb-2 text-white uppercase">FlexBot AI Access</h3>
            <p className="text-gray-400 text-sm mb-6 font-medium leading-relaxed">Select your lifetime license below.</p>

            <button
              onClick={() => onSelect({ type: 'LIFETIME', price: '$500' })}
              className="relative w-full flex flex-col group rounded-[28px] overflow-hidden text-left transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              {/* Layered backgrounds */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/[0.18] via-[#0a0a0a] to-[#080814]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.18),transparent_60%)]" />
              <div className="absolute inset-0 border border-white/10 group-hover:border-blue-500/40 rounded-[28px] transition-colors" />
              <div className="absolute -top-px left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-400/60 to-transparent" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent" />

              {/* Header */}
              <div className="relative px-6 pt-6 pb-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-1.5 bg-blue-500/10 text-blue-400 text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest border border-blue-500/20">
                    <Sparkles size={10} />
                    Official License
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest shadow-lg shadow-blue-500/30">Save $200</div>
                </div>
                <h4 className="text-2xl font-black text-white tracking-tighter uppercase mb-1">Lifetime License</h4>
                <p className="text-[11px] font-medium text-gray-500">Full access to FlexBot AI Protocol V5.0</p>
              </div>

              {/* Pricing */}
              <div className="relative px-6 py-5 border-y border-white/5">
                <div className="flex items-baseline gap-2.5">
                  <span className="text-5xl font-black bg-gradient-to-b from-white to-gray-300 bg-clip-text text-transparent tracking-tighter leading-none">$500</span>
                  <span className="text-gray-600 text-base font-bold line-through">$700</span>
                  <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest ml-auto">One-time</span>
                </div>
                <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-white/[0.03] border border-white/10 rounded-xl">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.6)] shrink-0" />
                  <span className="text-[10px] font-bold text-gray-500">Then</span>
                  <span className="text-sm font-black text-blue-400 tabular-nums">$30</span>
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">/ month — ongoing access</span>
                </div>
              </div>

              {/* Features */}
              <div className="relative px-6 py-5">
                <p className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em] mb-3">What's included</p>
                <div className="grid grid-cols-2 gap-y-2.5 gap-x-3">
                  {[
                    "FlexBot AI V5.0 Core",
                    "Lifetime Protocol Access",
                    "MT5 Signal Bridge",
                    "24/7 Deployment Support"
                  ].map((feat, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
                        <Check size={10} className="text-blue-400" strokeWidth={3} />
                      </div>
                      <span className="text-[10px] font-bold text-gray-300 leading-tight">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="relative px-6 pb-6">
                <div className="relative w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white py-4 rounded-xl font-black text-xs transition-all shadow-xl shadow-blue-500/20 uppercase tracking-widest flex items-center justify-center gap-2 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <span className="relative">Activate FlexBot AI</span>
                  <ArrowUpRight size={14} className="relative group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
            </button>
          </>
        )}
        {step === 'pay' && (
          <>
            <h3 className="text-3xl font-black tracking-tighter mb-2 text-white uppercase">Send Payment</h3>
            <p className="text-gray-400 mb-8 font-medium italic leading-relaxed px-12">
              Transfer {selectedPlan.price} USDT (BEP-20) for the FlexBot AI License. 
              ($30/mo subscription applies to maintain connection).
            </p>
            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-8">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Deposit Address (USDT BEP-20)</p>
              <div className="flex items-center gap-3 bg-black rounded-xl p-4 border border-white/5 mb-4 group"><code className="text-blue-400 text-xs font-mono break-all flex-1">{usdtAddress}</code><button onClick={() => navigator.clipboard.writeText(usdtAddress)} className="p-2 bg-white/5 hover:bg-blue-600 rounded-lg text-white transition-all active:scale-90"><Copy size={16} /></button></div>
              <div className="flex items-center justify-center gap-2 text-[10px] font-black text-yellow-500 uppercase"><Shield size={14} />Verify network is Binance Smart Chain</div>
            </div>
            <button onClick={onConfirm} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-5 rounded-2xl font-black text-lg transition-all shadow-xl">I HAVE TRANSFERRED THE FUNDS</button>
          </>
        )}
        {step === 'confirm' && (
          <>
            <div className="w-20 h-20 bg-green-500/10 border border-green-500/20 rounded-3xl flex items-center justify-center text-green-500 mx-auto mb-8">
              <Shield size={32} />
            </div>
            <h3 className="text-3xl font-black tracking-tighter mb-4 text-white uppercase">Almost Done</h3>
            <p className="text-gray-400 mb-4 font-medium leading-relaxed px-6">
              Send us your <span className="text-white">transaction hash</span> on Telegram so we can verify your payment and deliver the EA to your account.
            </p>
            <p className="text-gray-500 text-xs mb-10 italic">You'll typically receive access within a few hours.</p>
            <div className="space-y-4">
              <a
                href="https://t.me/flexbotdev?text=Hi%2C%20I%20just%20sent%20my%20payment%20for%20FlexBot%20AI.%20My%20tx%20hash%3A%20"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white py-5 rounded-2xl font-black text-lg transition-all shadow-xl flex items-center justify-center gap-3"
              >
                SEND TX HASH ON TELEGRAM
                <ArrowUpRight size={20} />
              </a>
              <button onClick={onClose} className="w-full bg-white/5 hover:bg-white/10 text-gray-400 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">Close</button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

// --- Main App ---
function App() {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState('select');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [tradingLogs, setTradingLogs] = useState([
    { type: 'SYSTEM', msg: "NEURAL CORE INITIALIZED", meta: "v5.0.4" },
    { type: 'BRIDGE', msg: "METATRADER 5 LINK ESTABLISHED", meta: "ID: 884210" },
    { type: 'SCAN', msg: "GOLD LIQUIDITY SYNC COMPLETE", meta: "latency: 12ms" },
  ]);

  const usdtAddress = "0xada3abe97b3c5b03aaf3756bbfc99a5722611927"; // Officieel USDT Wallet Adres (BEP-20)

  const handleSelectPlan = (plan) => { setSelectedPlan(plan); setPaymentStep('pay'); };
  const confirmPayment = () => { setPaymentStep('confirm'); };
  const closeModal = () => { setShowPaymentModal(false); setTimeout(() => setPaymentStep('select'), 300); };

  useEffect(() => {
    const interval = setInterval(() => {
      const action = Math.random() > 0.5 ? "BUY" : "SELL";
      const confidence = (Math.random() * 5 + 92).toFixed(2);
      const newLog = { type: 'TRADE', msg: `MT5 SIGNAL: AI ${action} GOLD (XAU/USD)`, meta: `CONF: ${confidence}% • NODE: ${Math.floor(Math.random() * 12) + 1}`, time: new Date().toLocaleTimeString([], { hour12: false }) };
      setTradingLogs(prev => [newLog, ...prev].slice(0, 15));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-blue-500/30 relative overflow-x-hidden">
      <BackgroundEffects />
      <SoundToggle />
      <div className="relative z-10">
        <LiveTradeTicker />
        <Navbar onBuyClick={() => setShowPaymentModal(true)} />
        <PaymentModal isOpen={showPaymentModal} step={paymentStep} onSelect={handleSelectPlan} onConfirm={confirmPayment} onClose={closeModal} selectedPlan={selectedPlan} usdtAddress={usdtAddress} />
        <Routes>
          <Route path="/" element={<LandingPage onBuyClick={() => setShowPaymentModal(true)} tradingLogs={tradingLogs} />} />
          <Route path="/dashboard" element={<Dashboard tradingLogs={tradingLogs} onBuyClick={() => setShowPaymentModal(true)} />} />
          <Route path="/how-it-works" element={<ContractPage onBuyClick={() => setShowPaymentModal(true)} />} />
          <Route path="/results" element={<ResultsPage />} />
          <Route path="/myfxbook" element={<MyfxbookPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/daily-report" element={<ReportPage period="day" />} />
          <Route path="/weekly-report" element={<ReportPage period="week" />} />
        </Routes>
        <Routes>
          <Route path="/" element={<footer className="container mx-auto px-4 sm:px-6 py-10 sm:py-20 flex flex-col md:flex-row justify-between items-center gap-6 sm:gap-10 border-t border-white/5"><Logo /><p className="text-xs font-bold text-gray-600 tracking-widest uppercase">&copy; 2026 All Rights Reserved.</p><div className="flex gap-6 text-xs font-black text-gray-500 uppercase tracking-widest"><a href="https://t.me/flexbotcommunity" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Telegram</a></div></footer>} />
          <Route path="*" element={null} />
        </Routes>
      </div>
    </div>
  );
}

export default App;
